import { __decorate, __param } from './external/tslib/tslib.es6.js';
import { mainWindow } from './vscode/src/vs/base/browser/window.js';
import { Event, DynamicListEventMultiplexer } from './vscode/src/vs/base/common/event.js';
import { Disposable } from './vscode/src/vs/base/common/lifecycle.js';
import { ResourceSet } from './vscode/src/vs/base/common/map.js';
import { OS } from './vscode/src/vs/base/common/platform.js';
import { joinPath } from './vscode/src/vs/base/common/resources.js';
import { URI } from './vscode/src/vs/base/common/uri.js';
import { ICodeEditorService } from './vscode/src/vs/editor/browser/services/codeEditorService.js';
import { IModelService } from './vscode/src/vs/editor/common/services/model.js';
import { ITreeViewsDnDService } from './vscode/src/vs/editor/common/services/treeViewsDndService.js';
import { StandaloneServices } from './vscode/src/vs/editor/standalone/browser/standaloneServices.js';
import { IContextKeyService } from './vscode/src/vs/platform/contextkey/common/contextkey.service.js';
import { IExtensionHostDebugService } from './vscode/src/vs/platform/debug/common/extensionHostDebug.service.js';
import { IDiagnosticsService } from './vscode/src/vs/platform/diagnostics/common/diagnostics.service.js';
import { NullDiagnosticsService } from './vscode/src/vs/platform/diagnostics/common/diagnostics.js';
import { IFileDialogService } from './vscode/src/vs/platform/dialogs/common/dialogs.service.js';
import { IDownloadService } from './vscode/src/vs/platform/download/common/download.service.js';
import { IEncryptionService } from './vscode/src/vs/platform/encryption/common/encryptionService.service.js';
import { IEnvironmentService } from './vscode/src/vs/platform/environment/common/environment.service.js';
import { IExtensionTipsService, IGlobalExtensionEnablementService, IExtensionGalleryService, IExtensionManagementService } from './vscode/src/vs/platform/extensionManagement/common/extensionManagement.service.js';
import { IExtensionStorageService } from './vscode/src/vs/platform/extensionManagement/common/extensionStorage.service.js';
import { IExtensionsProfileScannerService } from './vscode/src/vs/platform/extensionManagement/common/extensionsProfileScannerService.service.js';
import { IExtensionsScannerService } from './vscode/src/vs/platform/extensionManagement/common/extensionsScannerService.service.js';
import { IExtensionRecommendationNotificationService } from './vscode/src/vs/platform/extensionRecommendations/common/extensionRecommendations.service.js';
import { IExtensionResourceLoaderService } from './vscode/src/vs/platform/extensionResourceLoader/common/extensionResourceLoader.service.js';
import { IBuiltinExtensionsScannerService } from './vscode/src/vs/platform/extensions/common/extensions.service.js';
import { IFileService } from './vscode/src/vs/platform/files/common/files.service.js';
import { registerSingleton } from './vscode/src/vs/platform/instantiation/common/extensions.js';
import { IKeyboardLayoutService } from './vscode/src/vs/platform/keyboardLayout/common/keyboardLayout.service.js';
import { ILanguagePackService } from './vscode/src/vs/platform/languagePacks/common/languagePacks.service.js';
import { AbstractLoggerService, LogLevel, NullLogger } from './vscode/src/vs/platform/log/common/log.js';
import { ILoggerService } from './vscode/src/vs/platform/log/common/log.service.js';
import { NullPolicyService } from './vscode/src/vs/platform/policy/common/policy.js';
import { IPolicyService } from './vscode/src/vs/platform/policy/common/policy.service.js';
import { IProductService } from './vscode/src/vs/platform/product/common/productService.service.js';
import { IRemoteAuthorityResolverService } from './vscode/src/vs/platform/remote/common/remoteAuthorityResolver.service.js';
import { IRemoteExtensionsScannerService } from './vscode/src/vs/platform/remote/common/remoteExtensionsScanner.service.js';
import { IRemoteSocketFactoryService } from './vscode/src/vs/platform/remote/common/remoteSocketFactoryService.service.js';
import { IRequestService } from './vscode/src/vs/platform/request/common/request.service.js';
import { ISecretStorageService } from './vscode/src/vs/platform/secrets/common/secrets.service.js';
import { ISignService } from './vscode/src/vs/platform/sign/common/sign.service.js';
import { ICustomEndpointTelemetryService } from './vscode/src/vs/platform/telemetry/common/telemetry.service.js';
import { NullEndpointTelemetryService } from './vscode/src/vs/platform/telemetry/common/telemetryUtils.js';
import { TerminalLocation } from './vscode/src/vs/platform/terminal/common/terminal.js';
import { ITerminalLogService } from './vscode/src/vs/platform/terminal/common/terminal.service.js';
import { ITunnelService } from './vscode/src/vs/platform/tunnel/common/tunnel.service.js';
import { State } from './vscode/src/vs/platform/update/common/update.js';
import { IUpdateService } from './vscode/src/vs/platform/update/common/update.service.js';
import { IUriIdentityService } from './vscode/src/vs/platform/uriIdentity/common/uriIdentity.service.js';
import { UriIdentityService } from './vscode/src/vs/platform/uriIdentity/common/uriIdentityService.js';
import { IURLService } from './vscode/src/vs/platform/url/common/url.service.js';
import { IUserDataProfilesService } from './vscode/src/vs/platform/userDataProfile/common/userDataProfile.service.js';
import { IIgnoredExtensionsManagementService } from './vscode/src/vs/platform/userDataSync/common/ignoredExtensions.service.js';
import { IUserDataAutoSyncService, IUserDataSyncEnablementService, IUserDataSyncStoreManagementService, IUserDataSyncStoreService, IUserDataSyncLogService, IUserDataSyncService, IUserDataSyncResourceProviderService, IUserDataSyncLocalStoreService, IUserDataSyncUtilService } from './vscode/src/vs/platform/userDataSync/common/userDataSync.service.js';
import { IUserDataSyncAccountService } from './vscode/src/vs/platform/userDataSync/common/userDataSyncAccount.service.js';
import { IUserDataSyncMachinesService } from './vscode/src/vs/platform/userDataSync/common/userDataSyncMachines.service.js';
import { ICanonicalUriService } from './vscode/src/vs/platform/workspace/common/canonicalUri.service.js';
import { IEditSessionIdentityService } from './vscode/src/vs/platform/workspace/common/editSessions.service.js';
import { IWorkspaceTrustRequestService, IWorkspaceTrustEnablementService } from './vscode/src/vs/platform/workspace/common/workspaceTrust.service.js';
import { IWorkspacesService } from './vscode/src/vs/platform/workspaces/common/workspaces.service.js';
import { IExtensionStatusBarItemService } from './vscode/src/vs/workbench/api/browser/statusBarService.js';
import { IBreadcrumbsService } from './vscode/src/vs/workbench/browser/parts/editor/breadcrumbs.service.js';
import { DEFAULT_EDITOR_PART_OPTIONS } from './vscode/src/vs/workbench/browser/parts/editor/editor.js';
import { IViewDescriptorService } from './vscode/src/vs/workbench/common/views.service.js';
import { IAccessibleViewService } from './vscode/src/vs/workbench/contrib/accessibility/browser/accessibleView.service.js';
import { IQuickChatService, IChatWidgetService, IChatAccessibilityService, IChatCodeBlockContextProviderService } from './vscode/src/vs/workbench/contrib/chat/browser/chat.service.js';
import { IChatAgentService } from './vscode/src/vs/workbench/contrib/chat/common/chatAgents.service.js';
import { IChatService } from './vscode/src/vs/workbench/contrib/chat/common/chatService.service.js';
import { IChatSlashCommandService } from './vscode/src/vs/workbench/contrib/chat/common/chatSlashCommands.service.js';
import { IChatVariablesService } from './vscode/src/vs/workbench/contrib/chat/common/chatVariables.service.js';
import { IChatWidgetHistoryService } from './vscode/src/vs/workbench/contrib/chat/common/chatWidgetHistoryService.service.js';
import { ILanguageModelsService } from './vscode/src/vs/workbench/contrib/chat/common/languageModels.service.js';
import { ICommentService } from './vscode/src/vs/workbench/contrib/comments/browser/commentService.service.js';
import { ICustomEditorService } from './vscode/src/vs/workbench/contrib/customEditor/common/customEditor.service.js';
import { IDebugService } from './vscode/src/vs/workbench/contrib/debug/common/debug.service.js';
import { IDebugVisualizerService } from './vscode/src/vs/workbench/contrib/debug/common/debugVisualizers.service.js';
import { IEditSessionsLogService, IEditSessionsStorageService } from './vscode/src/vs/workbench/contrib/editSessions/common/editSessions.service.js';
import { IExtensionsWorkbenchService } from './vscode/src/vs/workbench/contrib/extensions/common/extensions.service.js';
import { IExternalUriOpenerService } from './vscode/src/vs/workbench/contrib/externalUriOpener/common/externalUriOpenerService.service.js';
import { IExplorerService } from './vscode/src/vs/workbench/contrib/files/browser/files.service.js';
import { IInlineChatSavingService } from './vscode/src/vs/workbench/contrib/inlineChat/browser/inlineChatSavingService.service.js';
import { IInlineChatSessionService } from './vscode/src/vs/workbench/contrib/inlineChat/browser/inlineChatSessionService.service.js';
import { IInlineChatService } from './vscode/src/vs/workbench/contrib/inlineChat/common/inlineChat.service.js';
import { IInteractiveDocumentService } from './vscode/src/vs/workbench/contrib/interactive/browser/interactiveDocumentService.service.js';
import { IInteractiveHistoryService } from './vscode/src/vs/workbench/contrib/interactive/browser/interactiveHistoryService.service.js';
import { IDefaultLogLevelsService } from './vscode/src/vs/workbench/contrib/logs/common/defaultLogLevels.service.js';
import { IMultiDiffSourceResolverService } from './vscode/src/vs/workbench/contrib/multiDiffEditor/browser/multiDiffSourceResolverService.service.js';
import { INotebookEditorService } from './vscode/src/vs/workbench/contrib/notebook/browser/services/notebookEditorService.service.js';
import { INotebookCellStatusBarService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookCellStatusBarService.service.js';
import { INotebookEditorModelResolverService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookEditorModelResolverService.service.js';
import { INotebookExecutionService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookExecutionService.service.js';
import { INotebookExecutionStateService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookExecutionStateService.service.js';
import { INotebookKernelService, INotebookKernelHistoryService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookKernelService.service.js';
import { INotebookKeymapService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookKeymapService.service.js';
import { INotebookLoggingService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookLoggingService.service.js';
import { INotebookRendererMessagingService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookRendererMessagingService.service.js';
import { INotebookService } from './vscode/src/vs/workbench/contrib/notebook/common/notebookService.service.js';
import { INotebookEditorWorkerService } from './vscode/src/vs/workbench/contrib/notebook/common/services/notebookWorkerService.service.js';
import { IOutputChannelModelService } from './vscode/src/vs/workbench/contrib/output/common/outputChannelModelService.service.js';
import { IPreferencesSearchService } from './vscode/src/vs/workbench/contrib/preferences/common/preferences.service.js';
import { IQuickDiffService } from './vscode/src/vs/workbench/contrib/scm/common/quickDiff.service.js';
import { ISCMService, ISCMViewService } from './vscode/src/vs/workbench/contrib/scm/common/scm.service.js';
import { IReplaceService } from './vscode/src/vs/workbench/contrib/search/browser/replace.service.js';
import { ISearchViewModelWorkbenchService } from './vscode/src/vs/workbench/contrib/search/browser/searchModel.service.js';
import { INotebookSearchService } from './vscode/src/vs/workbench/contrib/search/common/notebookSearch.service.js';
import { ISearchHistoryService } from './vscode/src/vs/workbench/contrib/search/common/searchHistoryService.service.js';
import { IShareService } from './vscode/src/vs/workbench/contrib/share/common/share.service.js';
import { ISnippetsService } from './vscode/src/vs/workbench/contrib/snippets/browser/snippets.service.js';
import { ISpeechService } from './vscode/src/vs/workbench/contrib/speech/common/speechService.service.js';
import { NoOpWorkspaceTagsService } from './vscode/src/vs/workbench/contrib/tags/browser/workspaceTagsService.js';
import { IWorkspaceTagsService } from './vscode/src/vs/workbench/contrib/tags/common/workspaceTags.service.js';
import { ITaskService } from './vscode/src/vs/workbench/contrib/tasks/common/taskService.service.js';
import { ITerminalService, ITerminalConfigurationService, ITerminalEditorService, ITerminalGroupService, ITerminalInstanceService } from './vscode/src/vs/workbench/contrib/terminal/browser/terminal.service.js';
import { IEnvironmentVariableService } from './vscode/src/vs/workbench/contrib/terminal/common/environmentVariable.service.js';
import { ITerminalProfileService, ITerminalProfileResolverService } from './vscode/src/vs/workbench/contrib/terminal/common/terminal.service.js';
import { ITerminalContributionService } from './vscode/src/vs/workbench/contrib/terminal/common/terminalExtensionPoints.service.js';
import { ITerminalLinkProviderService } from './vscode/src/vs/workbench/contrib/terminalContrib/links/browser/links.service.js';
import { ITerminalQuickFixService } from './vscode/src/vs/workbench/contrib/terminalContrib/quickFix/browser/quickFix.service.js';
import { ITestCoverageService } from './vscode/src/vs/workbench/contrib/testing/common/testCoverageService.service.js';
import { ITestExplorerFilterState } from './vscode/src/vs/workbench/contrib/testing/common/testExplorerFilterState.service.js';
import { ITestProfileService } from './vscode/src/vs/workbench/contrib/testing/common/testProfileService.service.js';
import { ITestResultService } from './vscode/src/vs/workbench/contrib/testing/common/testResultService.service.js';
import { ITestResultStorage } from './vscode/src/vs/workbench/contrib/testing/common/testResultStorage.service.js';
import { ITestService } from './vscode/src/vs/workbench/contrib/testing/common/testService.service.js';
import { ITestingContinuousRunService } from './vscode/src/vs/workbench/contrib/testing/common/testingContinuousRunService.service.js';
import { ITestingDecorationsService } from './vscode/src/vs/workbench/contrib/testing/common/testingDecorations.service.js';
import { ITestingPeekOpener } from './vscode/src/vs/workbench/contrib/testing/common/testingPeekOpener.service.js';
import { ITimelineService } from './vscode/src/vs/workbench/contrib/timeline/common/timeline.service.js';
import { IWebviewService } from './vscode/src/vs/workbench/contrib/webview/browser/webview.service.js';
import { IWebviewWorkbenchService } from './vscode/src/vs/workbench/contrib/webviewPanel/browser/webviewWorkbenchService.service.js';
import { IWebviewViewService } from './vscode/src/vs/workbench/contrib/webviewView/browser/webviewViewService.service.js';
import { IWalkthroughsService } from './vscode/src/vs/workbench/contrib/welcomeGettingStarted/browser/gettingStartedService.service.js';
import { IActivityService } from './vscode/src/vs/workbench/services/activity/common/activity.service.js';
import { IAiEmbeddingVectorService } from './vscode/src/vs/workbench/services/aiEmbeddingVector/common/aiEmbeddingVectorService.service.js';
import { IAiRelatedInformationService } from './vscode/src/vs/workbench/services/aiRelatedInformation/common/aiRelatedInformation.service.js';
import { IWorkbenchAssignmentService } from './vscode/src/vs/workbench/services/assignment/common/assignmentService.service.js';
import { IAuthenticationAccessService } from './vscode/src/vs/workbench/services/authentication/browser/authenticationAccessService.service.js';
import { IAuthenticationUsageService } from './vscode/src/vs/workbench/services/authentication/browser/authenticationUsageService.service.js';
import { IAuthenticationService, IAuthenticationExtensionsService } from './vscode/src/vs/workbench/services/authentication/common/authentication.service.js';
import { IAuxiliaryWindowService } from './vscode/src/vs/workbench/services/auxiliaryWindow/browser/auxiliaryWindowService.service.js';
import { IBannerService } from './vscode/src/vs/workbench/services/banner/browser/bannerService.service.js';
import { IJSONEditingService } from './vscode/src/vs/workbench/services/configuration/common/jsonEditing.service.js';
import { IConfigurationResolverService } from './vscode/src/vs/workbench/services/configurationResolver/common/configurationResolver.service.js';
import { IDecorationsService } from './vscode/src/vs/workbench/services/decorations/common/decorations.service.js';
import { ICustomEditorLabelService } from './vscode/src/vs/workbench/services/editor/common/customEditorLabelService.service.js';
import { IEditorGroupsService } from './vscode/src/vs/workbench/services/editor/common/editorGroupsService.service.js';
import { IEditorPaneService } from './vscode/src/vs/workbench/services/editor/common/editorPaneService.service.js';
import { IEditorResolverService } from './vscode/src/vs/workbench/services/editor/common/editorResolverService.service.js';
import { IEditorService } from './vscode/src/vs/workbench/services/editor/common/editorService.service.js';
import { IExtensionBisectService } from './vscode/src/vs/workbench/services/extensionManagement/browser/extensionBisect.service.js';
import { IExtensionFeaturesManagementService } from './vscode/src/vs/workbench/services/extensionManagement/common/extensionFeatures.service.js';
import { IExtensionManagementServerService, IWebExtensionsScannerService, IWorkbenchExtensionEnablementService, IWorkbenchExtensionManagementService } from './vscode/src/vs/workbench/services/extensionManagement/common/extensionManagement.service.js';
import { IExtensionRecommendationsService, IExtensionIgnoredRecommendationsService } from './vscode/src/vs/workbench/services/extensionRecommendations/common/extensionRecommendations.service.js';
import { IWorkspaceExtensionsConfigService } from './vscode/src/vs/workbench/services/extensionRecommendations/common/workspaceExtensionsConfig.service.js';
import { IExtensionUrlHandler } from './vscode/src/vs/workbench/services/extensions/browser/extensionUrlHandler.service.js';
import { IExtensionManifestPropertiesService } from './vscode/src/vs/workbench/services/extensions/common/extensionManifestPropertiesService.service.js';
import { IExtensionService } from './vscode/src/vs/workbench/services/extensions/common/extensions.service.js';
import { IElevatedFileService } from './vscode/src/vs/workbench/services/files/common/elevatedFileService.service.js';
import { IFilesConfigurationService } from './vscode/src/vs/workbench/services/filesConfiguration/common/filesConfigurationService.service.js';
import { IHistoryService } from './vscode/src/vs/workbench/services/history/common/history.service.js';
import { IHostService } from './vscode/src/vs/workbench/services/host/browser/host.service.js';
import { ITroubleshootIssueService } from './vscode/src/vs/workbench/services/issue/browser/issueTroubleshoot.service.js';
import { IWorkbenchIssueService } from './vscode/src/vs/workbench/services/issue/common/issue.service.js';
import { FallbackKeyboardMapper } from './vscode/src/vs/workbench/services/keybinding/common/fallbackKeyboardMapper.js';
import { IKeybindingEditingService } from './vscode/src/vs/workbench/services/keybinding/common/keybindingEditing.service.js';
import { ILanguageDetectionService } from './vscode/src/vs/workbench/services/languageDetection/common/languageDetectionWorkerService.service.js';
import { ILanguageStatusService } from './vscode/src/vs/workbench/services/languageStatus/common/languageStatusService.service.js';
import { ILifecycleService } from './vscode/src/vs/workbench/services/lifecycle/common/lifecycle.service.js';
import { AbstractLifecycleService } from './vscode/src/vs/workbench/services/lifecycle/common/lifecycleService.js';
import { ILocaleService, IActiveLanguagePackService } from './vscode/src/vs/workbench/services/localization/common/locale.service.js';
import { INotebookDocumentService } from './vscode/src/vs/workbench/services/notebook/common/notebookDocumentService.service.js';
import { IOutlineService } from './vscode/src/vs/workbench/services/outline/browser/outline.service.js';
import { IOutputService } from './vscode/src/vs/workbench/services/output/common/output.service.js';
import { IPaneCompositePartService } from './vscode/src/vs/workbench/services/panecomposite/browser/panecomposite.service.js';
import { IPathService } from './vscode/src/vs/workbench/services/path/common/pathService.service.js';
import { IPreferencesService } from './vscode/src/vs/workbench/services/preferences/common/preferences.service.js';
import { IRemoteAgentService } from './vscode/src/vs/workbench/services/remote/common/remoteAgentService.service.js';
import { IRemoteExplorerService } from './vscode/src/vs/workbench/services/remote/common/remoteExplorerService.service.js';
import { ISearchService } from './vscode/src/vs/workbench/services/search/common/search.service.js';
import { IStatusbarService } from './vscode/src/vs/workbench/services/statusbar/browser/statusbar.service.js';
import { IEmbedderTerminalService } from './vscode/src/vs/workbench/services/terminal/common/embedderTerminalService.service.js';
import { ITextMateTokenizationService } from './vscode/src/vs/workbench/services/textMate/browser/textMateTokenizationFeature.service.js';
import { ITextEditorService } from './vscode/src/vs/workbench/services/textfile/common/textEditorService.service.js';
import { ITextFileService } from './vscode/src/vs/workbench/services/textfile/common/textfiles.service.js';
import { IHostColorSchemeService } from './vscode/src/vs/workbench/services/themes/common/hostColorSchemeService.service.js';
import { ITimerService } from './vscode/src/vs/workbench/services/timer/browser/timerService.service.js';
import { ITitleService } from './vscode/src/vs/workbench/services/title/browser/titleService.service.js';
import { IUntitledTextEditorService } from './vscode/src/vs/workbench/services/untitled/common/untitledTextEditorService.service.js';
import { IUserActivityService } from './vscode/src/vs/workbench/services/userActivity/common/userActivityService.service.js';
import { IUserDataInitializationService } from './vscode/src/vs/workbench/services/userData/browser/userDataInit.service.js';
import { IRemoteUserDataProfilesService } from './vscode/src/vs/workbench/services/userDataProfile/common/remoteUserDataProfiles.service.js';
import { IUserDataProfileService, IUserDataProfileImportExportService, IUserDataProfileManagementService } from './vscode/src/vs/workbench/services/userDataProfile/common/userDataProfile.service.js';
import { UserDataProfileService } from './vscode/src/vs/workbench/services/userDataProfile/common/userDataProfileService.js';
import { IUserDataSyncWorkbenchService } from './vscode/src/vs/workbench/services/userDataSync/common/userDataSync.service.js';
import { ITreeViewsService } from './vscode/src/vs/workbench/services/views/browser/treeViewsService.service.js';
import { IViewsService } from './vscode/src/vs/workbench/services/views/common/viewsService.service.js';
import { IWorkingCopyBackupService } from './vscode/src/vs/workbench/services/workingCopy/common/workingCopyBackup.service.js';
import { IWorkingCopyEditorService } from './vscode/src/vs/workbench/services/workingCopy/common/workingCopyEditorService.service.js';
import { IWorkingCopyFileService } from './vscode/src/vs/workbench/services/workingCopy/common/workingCopyFileService.service.js';
import { IWorkingCopyHistoryService } from './vscode/src/vs/workbench/services/workingCopy/common/workingCopyHistory.service.js';
import { IWorkingCopyService } from './vscode/src/vs/workbench/services/workingCopy/common/workingCopyService.service.js';
import { IWorkspaceEditingService } from './vscode/src/vs/workbench/services/workspaces/common/workspaceEditing.service.js';
import { IWorkspaceIdentityService } from './vscode/src/vs/workbench/services/workspaces/common/workspaceIdentityService.service.js';
import { NullExtensionService } from './vscode/src/vs/workbench/services/extensions/common/extensions.js';
import { toUserDataProfile } from './vscode/src/vs/platform/userDataProfile/common/userDataProfile.js';
import { ILanguageModelStatsService } from './vscode/src/vs/workbench/contrib/chat/common/languageModelStats.service.js';
import { IAccessibleViewInformationService } from './vscode/src/vs/workbench/services/accessibility/common/accessibleViewInformationService.service.js';
import { getExtensionIdProvidingCurrentLocale, getBuiltInExtensionTranslationsUris } from './l10n.js';
import { unsupported } from './tools.js';
var _a;
registerSingleton(ILoggerService, class NullLoggerService extends AbstractLoggerService {
    constructor() {
        super(LogLevel.Info, URI.file('logs.log'));
    }
    doCreateLogger() { return new NullLogger(); }
}, 0 );
registerSingleton(IEditorService, class EditorService {
    constructor() {
        this._serviceBrand = undefined;
        this.onWillOpenEditor = Event.None;
        this.onDidActiveEditorChange = Event.None;
        this.onDidVisibleEditorsChange = Event.None;
        this.onDidEditorsChange = Event.None;
        this.onDidCloseEditor = Event.None;
        this.activeEditorPane = undefined;
        this.activeEditor = undefined;
        this.activeTextEditorLanguageId = undefined;
        this.visibleEditorPanes = [];
        this.visibleEditors = [];
        this.visibleTextEditorControls = [];
        this.editors = [];
        this.count = 0;
        this.getEditors = () => [];
        this.openEditor = unsupported;
        this.openEditors = unsupported;
        this.replaceEditors = async () => { };
        this.isOpened = () => false;
        this.isVisible = () => false;
        this.closeEditor = async () => { };
        this.closeEditors = async () => { };
        this.findEditors = () => [];
        this.save = async () => ({ success: false, editors: [] });
        this.saveAll = async () => ({ success: false, editors: [] });
        this.revert = async () => false;
        this.revertAll = async () => false;
    }
    get activeTextEditorControl() { return StandaloneServices.get(ICodeEditorService).getFocusedCodeEditor() ?? undefined; }
    createScoped() {
        return this;
    }
}, 0 );
registerSingleton(IPaneCompositePartService, class PaneCompositePartService {
    constructor() {
        this._serviceBrand = undefined;
        this.onDidPaneCompositeOpen = Event.None;
        this.onDidPaneCompositeClose = Event.None;
        this.openPaneComposite = async () => undefined;
        this.getActivePaneComposite = () => undefined;
        this.getPaneComposite = () => undefined;
        this.getPaneComposites = () => [];
        this.getPinnedPaneCompositeIds = () => [];
        this.getVisiblePaneCompositeIds = () => [];
        this.getProgressIndicator = () => undefined;
        this.hideActivePaneComposite = () => { };
        this.getLastActivePaneCompositeId = unsupported;
        this.showActivity = () => Disposable.None;
    }
}, 0 );
registerSingleton(IUriIdentityService, UriIdentityService, 1 );
registerSingleton(ITextFileService, class TextFileService {
    constructor() {
        this.isDirty = unsupported;
        this.save = unsupported;
        this.saveAs = unsupported;
        this.revert = unsupported;
        this.read = unsupported;
        this.readStream = unsupported;
        this.write = unsupported;
        this.create = unsupported;
        this.getEncodedReadable = unsupported;
        this.getDecodedStream = unsupported;
        this.dispose = unsupported;
    }
    get files() {
        return unsupported();
    }
    get untitled() {
        return unsupported();
    }
    get encoding() {
        return unsupported();
    }
}, 0 );
registerSingleton(IFileService, class FileService {
    constructor() {
        this._serviceBrand = undefined;
        this.onDidChangeFileSystemProviderRegistrations = Event.None;
        this.onDidChangeFileSystemProviderCapabilities = Event.None;
        this.onWillActivateFileSystemProvider = Event.None;
        this.registerProvider = unsupported;
        this.getProvider = function () {
            return undefined;
        };
        this.activateProvider = async () => { };
        this.canHandleResource = async () => false;
        this.hasProvider = () => false;
        this.hasCapability = () => false;
        this.listCapabilities = () => [];
        this.onDidFilesChange = Event.None;
        this.onDidRunOperation = Event.None;
        this.resolve = unsupported;
        this.resolveAll = unsupported;
        this.stat = unsupported;
        this.exists = async () => false;
        this.readFile = unsupported;
        this.readFileStream = unsupported;
        this.writeFile = unsupported;
        this.move = unsupported;
        this.canMove = unsupported;
        this.copy = unsupported;
        this.canCopy = unsupported;
        this.cloneFile = unsupported;
        this.createFile = unsupported;
        this.canCreateFile = unsupported;
        this.createFolder = unsupported;
        this.del = unsupported;
        this.canDelete = unsupported;
        this.onDidWatchError = Event.None;
        this.watch = unsupported;
        this.createWatcher = unsupported;
    }
    dispose() {
    }
}, 0 );
class EmptyEditorGroup {
    constructor() {
        this.isTransient = () => false;
        this.windowId = mainWindow.vscodeWindowId;
        this.createEditorActions = unsupported;
        this.onDidFocus = Event.None;
        this.onDidOpenEditorFail = Event.None;
        this.whenRestored = Promise.resolve();
        this.disposed = false;
        this.setActive = unsupported;
        this.notifyIndexChanged = unsupported;
        this.relayout = unsupported;
        this.dispose = unsupported;
        this.toJSON = unsupported;
        this.minimumWidth = 0;
        this.maximumWidth = Number.POSITIVE_INFINITY;
        this.minimumHeight = 0;
        this.maximumHeight = Number.POSITIVE_INFINITY;
        this.onDidChange = Event.None;
        this.layout = unsupported;
        this.onDidModelChange = Event.None;
        this.onWillDispose = Event.None;
        this.onDidActiveEditorChange = Event.None;
        this.onWillCloseEditor = Event.None;
        this.onDidCloseEditor = Event.None;
        this.onWillMoveEditor = Event.None;
        this.onWillOpenEditor = Event.None;
        this.id = 0;
        this.index = 0;
        this.label = 'main';
        this.ariaLabel = 'main';
        this.activeEditorPane = undefined;
        this.activeEditor = null;
        this.previewEditor = null;
        this.count = 0;
        this.isEmpty = false;
        this.isLocked = false;
        this.stickyCount = 0;
        this.editors = [];
        this.getEditors = () => [];
        this.findEditors = () => [];
        this.getEditorByIndex = () => undefined;
        this.getIndexOfEditor = unsupported;
        this.openEditor = unsupported;
        this.openEditors = unsupported;
        this.isPinned = () => false;
        this.isSticky = () => false;
        this.isActive = () => false;
        this.contains = () => false;
        this.moveEditor = unsupported;
        this.moveEditors = unsupported;
        this.copyEditor = unsupported;
        this.copyEditors = unsupported;
        this.closeEditor = unsupported;
        this.closeEditors = unsupported;
        this.closeAllEditors = unsupported;
        this.replaceEditors = unsupported;
        this.pinEditor = () => { };
        this.stickEditor = () => { };
        this.unstickEditor = () => { };
        this.lock = () => { };
        this.isFirst = unsupported;
        this.isLast = unsupported;
    }
    get groupsView() {
        return unsupported();
    }
    notifyLabelChanged() { }
    get titleHeight() {
        return unsupported();
    }
    get element() {
        return unsupported();
    }
    get scopedContextKeyService() { return StandaloneServices.get(IContextKeyService); }
    focus() {
    }
}
const fakeActiveGroup = new EmptyEditorGroup();
class EmptyEditorPart {
    constructor() {
        this.windowId = mainWindow.vscodeWindowId;
        this.hasMaximizedGroup = () => false;
        this.onDidLayout = Event.None;
        this.onDidScroll = Event.None;
        this.isReady = true;
        this.whenReady = Promise.resolve();
        this.whenRestored = Promise.resolve();
        this.hasRestorableState = false;
        this.centerLayout = unsupported;
        this.isLayoutCentered = unsupported;
        this.enforcePartOptions = unsupported;
        this.onDidChangeActiveGroup = Event.None;
        this.onDidAddGroup = Event.None;
        this.onDidRemoveGroup = Event.None;
        this.onDidMoveGroup = Event.None;
        this.onDidActivateGroup = Event.None;
        this.onDidChangeGroupIndex = Event.None;
        this.onDidChangeGroupLocked = Event.None;
        this.onDidChangeGroupMaximized = Event.None;
        this.activeGroup = fakeActiveGroup;
        this.groups = [fakeActiveGroup];
        this.count = 0;
        this.orientation = 0 ;
        this.getGroups = () => [];
        this.getGroup = () => undefined;
        this.activateGroup = unsupported;
        this.getSize = unsupported;
        this.setSize = unsupported;
        this.arrangeGroups = unsupported;
        this.toggleMaximizeGroup = unsupported;
        this.toggleExpandGroup = unsupported;
        this.applyLayout = unsupported;
        this.getLayout = unsupported;
        this.setGroupOrientation = unsupported;
        this.findGroup = () => undefined;
        this.addGroup = unsupported;
        this.removeGroup = unsupported;
        this.moveGroup = unsupported;
        this.mergeGroup = unsupported;
        this.mergeAllGroups = unsupported;
        this.copyGroup = unsupported;
        this.partOptions = DEFAULT_EDITOR_PART_OPTIONS;
        this.onDidChangeEditorPartOptions = Event.None;
        this.createEditorDropTarget = unsupported;
    }
    get contentDimension() { return unsupported(); }
    get sideGroup() { return unsupported(); }
}
class EmptyEditorGroupsService {
    constructor() {
        this.saveWorkingSet = unsupported;
        this.getWorkingSets = unsupported;
        this.applyWorkingSet = unsupported;
        this.deleteWorkingSet = unsupported;
        this.onDidCreateAuxiliaryEditorPart = Event.None;
        this.mainPart = new EmptyEditorPart();
        this.activePart = this.mainPart;
        this.parts = [this.mainPart];
        this.getPart = unsupported;
        this.createAuxiliaryEditorPart = unsupported;
        this.onDidChangeGroupMaximized = Event.None;
        this.toggleMaximizeGroup = unsupported;
        this.toggleExpandGroup = unsupported;
        this.partOptions = DEFAULT_EDITOR_PART_OPTIONS;
        this.createEditorDropTarget = unsupported;
        this._serviceBrand = undefined;
        this.getLayout = unsupported;
        this.onDidChangeActiveGroup = Event.None;
        this.onDidAddGroup = Event.None;
        this.onDidRemoveGroup = Event.None;
        this.onDidMoveGroup = Event.None;
        this.onDidActivateGroup = Event.None;
        this.onDidLayout = Event.None;
        this.onDidScroll = Event.None;
        this.onDidChangeGroupIndex = Event.None;
        this.onDidChangeGroupLocked = Event.None;
        this.activeGroup = fakeActiveGroup;
        this.groups = [fakeActiveGroup];
        this.count = 0;
        this.orientation = 0 ;
        this.isReady = false;
        this.whenReady = Promise.resolve();
        this.whenRestored = Promise.resolve();
        this.hasRestorableState = false;
        this.getGroups = () => [];
        this.getGroup = () => undefined;
        this.activateGroup = unsupported;
        this.getSize = unsupported;
        this.setSize = unsupported;
        this.arrangeGroups = unsupported;
        this.applyLayout = unsupported;
        this.centerLayout = unsupported;
        this.isLayoutCentered = () => false;
        this.setGroupOrientation = unsupported;
        this.findGroup = () => undefined;
        this.addGroup = unsupported;
        this.removeGroup = unsupported;
        this.moveGroup = unsupported;
        this.mergeGroup = unsupported;
        this.mergeAllGroups = unsupported;
        this.copyGroup = unsupported;
        this.onDidChangeEditorPartOptions = Event.None;
        this.enforcePartOptions = unsupported;
    }
    get contentDimension() { return unsupported(); }
    get sideGroup() { return unsupported(); }
}
registerSingleton(IEditorGroupsService, EmptyEditorGroupsService, 0 );
registerSingleton(IBannerService, class BannerService {
    focus() { }
    focusNextAction() { }
    focusPreviousAction() { }
    hide() { }
    show() { }
}, 0 );
registerSingleton(ITitleService, class TitleService {
    constructor() {
        this.getPart = unsupported;
        this.createAuxiliaryTitlebarPart = unsupported;
        this.dispose = unsupported;
        this.onMenubarVisibilityChange = Event.None;
        this.isCommandCenterVisible = false;
        this.onDidChangeCommandCenterVisibility = Event.None;
        this.registerVariables = () => { };
    }
    updateProperties() { }
}, 0 );
registerSingleton(IWorkingCopyFileService, class WorkingCopyFileService {
    constructor() {
        this.onWillRunWorkingCopyFileOperation = Event.None;
        this.onDidFailWorkingCopyFileOperation = Event.None;
        this.onDidRunWorkingCopyFileOperation = Event.None;
        this.addFileOperationParticipant = unsupported;
        this.hasSaveParticipants = false;
        this.addSaveParticipant = unsupported;
        this.runSaveParticipants = unsupported;
        this.create = unsupported;
        this.createFolder = unsupported;
        this.move = unsupported;
        this.copy = unsupported;
        this.delete = unsupported;
        this.registerWorkingCopyProvider = unsupported;
        this.getDirty = () => [];
    }
}, 0 );
registerSingleton(IPathService, class PathService {
    constructor() {
        this.defaultUriScheme = 'file';
        this.fileURI = unsupported;
        this.userHome = unsupported;
        this.hasValidBasename = unsupported;
        this.resolvedUserHome = undefined;
    }
    get path() {
        return unsupported();
    }
}, 1 );
registerSingleton(IProductService, class ProductService {
    constructor() {
        this._serviceBrand = undefined;
        this.version = "1.89.1";
        this.commit = "dc96b837cf6bb4af9cd736aa3af08cf8279f7685";
        this.quality = 'oss';
        this.nameShort = 'Code - OSS Dev';
        this.nameLong = 'Code - OSS Dev';
        this.applicationName = 'code-oss';
        this.dataFolderName = '.vscode-oss';
        this.urlProtocol = 'code-oss';
        this.reportIssueUrl = 'https://github.com/microsoft/vscode/issues/new';
        this.licenseName = 'MIT';
        this.licenseUrl = 'https://github.com/microsoft/vscode/blob/main/LICENSE.txt';
        this.serverApplicationName = 'code-server-oss';
    }
}, 0 );
registerSingleton(IExtensionTipsService, class ExtensionTipsService {
    constructor() {
        this._serviceBrand = undefined;
        this.getConfigBasedTips = async () => [];
        this.getImportantExecutableBasedTips = async () => [];
        this.getOtherExecutableBasedTips = async () => [];
    }
}, 0 );
registerSingleton(ILanguageStatusService, class LanguageStatusService {
    constructor() {
        this.onDidChange = Event.None;
        this.addStatus = unsupported;
        this.getLanguageStatus = unsupported;
    }
}, 1 );
registerSingleton(IHostService, class HostService {
    constructor() {
        this.onDidChangeFullScreen = Event.None;
        this.onDidChangeFocus = Event.None;
        this.hasFocus = false;
        this.hadLastFocus = async () => false;
        this.focus = unsupported;
        this.onDidChangeActiveWindow = Event.None;
        this.openWindow = unsupported;
        this.toggleFullScreen = unsupported;
        this.moveTop = unsupported;
        this.getCursorScreenPoint = unsupported;
        this.restart = unsupported;
        this.reload = unsupported;
        this.close = unsupported;
        this.withExpectedShutdown = unsupported;
    }
}, 0 );
registerSingleton(ILifecycleService, class LifecycleService extends AbstractLifecycleService {
    constructor() {
        super(...arguments);
        this.shutdown = unsupported;
    }
}, 0 );
registerSingleton(ILanguageDetectionService, class LanguageDetectionService {
    isEnabledForLanguage() {
        return false;
    }
    async detectLanguage() {
        return undefined;
    }
}, 0 );
registerSingleton(IExtensionService, NullExtensionService, 0 );
registerSingleton(IKeyboardLayoutService, class KeyboardLayoutService {
    constructor() {
        this.onDidChangeKeyboardLayout = Event.None;
        this.getRawKeyboardMapping = () => null;
        this.getCurrentKeyboardLayout = () => null;
        this.getAllKeyboardLayouts = () => [];
        this.getKeyboardMapper = () => new FallbackKeyboardMapper(false, OS);
        this.validateCurrentKeyboardMapping = () => { };
    }
}, 1 );
registerSingleton(IUserDataInitializationService, class NullUserDataInitializationService {
    async requiresInitialization() {
        return false;
    }
    async whenInitializationFinished() { }
    async initializeRequiredResources() { }
    async initializeInstalledExtensions() { }
    async initializeOtherResources() { }
}, 0 );
registerSingleton(IHostColorSchemeService, class HostColorSchemeService {
    constructor() {
        this.dark = false;
        this.highContrast = false;
        this.onDidChangeColorScheme = Event.None;
    }
}, 0 );
let PreferencesService = class PreferencesService {
    constructor(profileService) {
        this.profileService = profileService;
        this.userSettingsResource = this.profileService.currentProfile.settingsResource;
        this.workspaceSettingsResource = null;
        this.getFolderSettingsResource = unsupported;
        this.createPreferencesEditorModel = unsupported;
        this.resolveModel = unsupported;
        this.createSettings2EditorModel = unsupported;
        this.openRawDefaultSettings = unsupported;
        this.openSettings = unsupported;
        this.openUserSettings = unsupported;
        this.openRemoteSettings = unsupported;
        this.openWorkspaceSettings = unsupported;
        this.openFolderSettings = unsupported;
        this.openGlobalKeybindingSettings = unsupported;
        this.openDefaultKeybindingsFile = unsupported;
        this.getEditableSettingsURI = unsupported;
        this.createSplitJsonEditorInput = unsupported;
        this.openApplicationSettings = unsupported;
        this.openLanguageSpecificSettings = unsupported;
    }
};
PreferencesService = __decorate([
    ( __param(0, IUserDataProfileService))
], PreferencesService);
registerSingleton(IPreferencesService, PreferencesService, 0 );
registerSingleton(ITextMateTokenizationService, class NullTextMateService {
    constructor() {
        this.onDidEncounterLanguage = Event.None;
        this.createGrammar = unsupported;
        this.startDebugMode = unsupported;
        this.createTokenizer = unsupported;
    }
}, 0 );
let UserDataProfilesService = class UserDataProfilesService {
    constructor(profileService) {
        this.profileService = profileService;
        this.onDidResetWorkspaces = Event.None;
        this.isEnabled = () => false;
        this.createNamedProfile = unsupported;
        this.createTransientProfile = unsupported;
        this.resetWorkspaces = unsupported;
        this.cleanUp = unsupported;
        this.cleanUpTransientProfiles = unsupported;
        this.defaultProfile = this.profileService.currentProfile;
        this.onDidChangeProfiles = Event.None;
        this.profiles = [this.profileService.currentProfile];
        this.createProfile = unsupported;
        this.updateProfile = unsupported;
        this.setProfileForWorkspace = unsupported;
        this.getProfile = () => this.profileService.currentProfile;
        this.removeProfile = unsupported;
    }
    get profilesHome() { return unsupported(); }
};
UserDataProfilesService = __decorate([
    ( __param(0, IUserDataProfileService))
], UserDataProfilesService);
registerSingleton(IUserDataProfilesService, UserDataProfilesService, 0 );
let InjectedUserDataProfileService = class InjectedUserDataProfileService extends UserDataProfileService {
    constructor(environmentService) {
        super({
            ...toUserDataProfile('__default__profile__', 'Default', environmentService.userRoamingDataHome, joinPath(environmentService.cacheHome, 'CachedProfilesData')),
            isDefault: true
        });
    }
};
InjectedUserDataProfileService = __decorate([
    ( __param(0, IEnvironmentService))
], InjectedUserDataProfileService);
registerSingleton(IUserDataProfileService, InjectedUserDataProfileService, 0 );
registerSingleton(IPolicyService, NullPolicyService, 0 );
registerSingleton(ISnippetsService, class SnippetsService {
    constructor() {
        this.getSnippetFiles = unsupported;
        this.isEnabled = unsupported;
        this.updateEnablement = unsupported;
        this.updateUsageTimestamp = unsupported;
        this.getSnippets = async () => [];
        this.getSnippetsSync = unsupported;
    }
}, 0 );
const debugModel = {
    getSession: () => undefined,
    getSessions: () => [],
    getBreakpoints: () => [],
    areBreakpointsActivated: () => false,
    getFunctionBreakpoints: () => [],
    getDataBreakpoints: () => [],
    getExceptionBreakpoints: () => [],
    getExceptionBreakpointsForSession: () => [],
    getInstructionBreakpoints: () => [],
    getWatchExpressions: () => [],
    onDidChangeBreakpoints: Event.None,
    onDidChangeCallStack: Event.None,
    onDidChangeWatchExpressions: Event.None,
    fetchCallstack: unsupported,
    getId: unsupported,
    registerBreakpointModes: unsupported,
    getBreakpointModes: () => []
};
class FakeViewModel {
    constructor() {
        this.setVisualizedExpression = unsupported;
        this.getVisualizedExpression = () => undefined;
        this.onDidChangeVisualization = Event.None;
        this.getId = unsupported;
        this.focusedSession = undefined;
        this.focusedThread = undefined;
        this.focusedStackFrame = undefined;
        this.getSelectedExpression = unsupported;
        this.setSelectedExpression = unsupported;
        this.updateViews = unsupported;
        this.isMultiSessionView = unsupported;
        this.onDidFocusSession = Event.None;
        this.onDidFocusStackFrame = Event.None;
        this.onDidSelectExpression = Event.None;
        this.onDidEvaluateLazyExpression = Event.None;
        this.onWillUpdateViews = Event.None;
        this.onDidFocusThread = Event.None;
        this.evaluateLazyExpression = unsupported;
    }
}
class FakeAdapterManager {
    constructor() {
        this.onDidRegisterDebugger = Event.None;
        this.hasEnabledDebuggers = () => false;
        this.getDebugAdapterDescriptor = unsupported;
        this.getDebuggerLabel = unsupported;
        this.someDebuggerInterestedInLanguage = () => false;
        this.getDebugger = () => undefined;
        this.activateDebuggers = unsupported;
        this.registerDebugAdapterFactory = () => Disposable.None;
        this.createDebugAdapter = unsupported;
        this.registerDebugAdapterDescriptorFactory = unsupported;
        this.unregisterDebugAdapterDescriptorFactory = unsupported;
        this.substituteVariables = unsupported;
        this.runInTerminal = unsupported;
        this.getEnabledDebugger = unsupported;
        this.guessDebugger = unsupported;
        this.onDidDebuggersExtPointRead = Event.None;
    }
}
registerSingleton(IDebugService, class DebugService {
    constructor() {
        this.initializingOptions = undefined;
        this.sendBreakpoints = unsupported;
        this.updateDataBreakpoint = unsupported;
        this.onDidChangeState = Event.None;
        this.onDidNewSession = Event.None;
        this.onWillNewSession = Event.None;
        this.onDidEndSession = Event.None;
        this.getConfigurationManager = unsupported;
        this.getAdapterManager = () => new FakeAdapterManager();
        this.focusStackFrame = unsupported;
        this.canSetBreakpointsIn = unsupported;
        this.addBreakpoints = unsupported;
        this.updateBreakpoints = unsupported;
        this.enableOrDisableBreakpoints = unsupported;
        this.setBreakpointsActivated = unsupported;
        this.removeBreakpoints = unsupported;
        this.addFunctionBreakpoint = unsupported;
        this.updateFunctionBreakpoint = unsupported;
        this.removeFunctionBreakpoints = unsupported;
        this.addDataBreakpoint = unsupported;
        this.removeDataBreakpoints = unsupported;
        this.addInstructionBreakpoint = unsupported;
        this.removeInstructionBreakpoints = unsupported;
        this.setExceptionBreakpointCondition = unsupported;
        this.setExceptionBreakpointsForSession = unsupported;
        this.sendAllBreakpoints = unsupported;
        this.addWatchExpression = unsupported;
        this.renameWatchExpression = unsupported;
        this.moveWatchExpression = unsupported;
        this.removeWatchExpressions = unsupported;
        this.startDebugging = unsupported;
        this.restartSession = unsupported;
        this.stopSession = unsupported;
        this.sourceIsNotAvailable = unsupported;
        this.getModel = () => debugModel;
        this.getViewModel = () => new FakeViewModel();
        this.runTo = unsupported;
    }
    get state() { return unsupported(); }
}, 0 );
registerSingleton(IRequestService, class RequestService {
    constructor() {
        this.request = unsupported;
        this.resolveProxy = unsupported;
        this.loadCertificates = unsupported;
    }
}, 0 );
registerSingleton(IWorkspaceTrustRequestService, class WorkspaceTrustRequestService {
    constructor() {
        this.onDidInitiateOpenFilesTrustRequest = Event.None;
        this.onDidInitiateWorkspaceTrustRequest = Event.None;
        this.onDidInitiateWorkspaceTrustRequestOnStartup = Event.None;
        this.completeOpenFilesTrustRequest = unsupported;
        this.requestOpenFilesTrust = async () => 1 ;
        this.cancelWorkspaceTrustRequest = unsupported;
        this.completeWorkspaceTrustRequest = unsupported;
        this.requestWorkspaceTrust = async () => true;
        this.requestWorkspaceTrustOnStartup = () => null;
    }
}, 0 );
registerSingleton(IActivityService, class ActivityService {
    constructor() {
        this.onDidChangeActivity = Event.None;
        this.getViewContainerActivities = unsupported;
        this.getActivity = unsupported;
        this.showViewContainerActivity = () => Disposable.None;
        this.showViewActivity = () => Disposable.None;
        this.showAccountsActivity = () => Disposable.None;
        this.showGlobalActivity = () => Disposable.None;
    }
}, 0 );
registerSingleton(IExtensionHostDebugService, class ExtensionHostDebugService {
    constructor() {
        this.reload = unsupported;
        this.onReload = Event.None;
        this.close = unsupported;
        this.onClose = Event.None;
        this.attachSession = unsupported;
        this.onAttachSession = Event.None;
        this.terminateSession = unsupported;
        this.onTerminateSession = Event.None;
        this.openExtensionDevelopmentHostWindow = unsupported;
    }
}, 0 );
registerSingleton(IViewsService, class ViewsService {
    constructor() {
        this.getFocusedViewName = unsupported;
        this.onDidChangeFocusedView = Event.None;
        this.onDidChangeViewContainerVisibility = Event.None;
        this.isViewContainerVisible = () => false;
        this.openViewContainer = unsupported;
        this.closeViewContainer = unsupported;
        this.getVisibleViewContainer = unsupported;
        this.getActiveViewPaneContainerWithId = () => null;
        this.onDidChangeViewVisibility = Event.None;
        this.isViewVisible = () => false;
        this.openView = async () => null;
        this.closeView = unsupported;
        this.getActiveViewWithId = () => null;
        this.getViewWithId = () => null;
        this.getViewProgressIndicator = () => undefined;
    }
}, 0 );
registerSingleton(IViewDescriptorService, class ViewDescriptorService {
    constructor() {
        this.viewContainers = [];
        this.onDidChangeViewContainers = Event.None;
        this.getDefaultViewContainer = () => undefined;
        this.getViewContainerById = () => null;
        this.isViewContainerRemovedPermanently = unsupported;
        this.getDefaultViewContainerLocation = () => null;
        this.getViewContainerLocation = () => null;
        this.getViewContainersByLocation = unsupported;
        this.getViewContainerModel = () => ({
            onDidChangeAllViewDescriptors: Event.None,
            visibleViewDescriptors: []
        });
        this.onDidChangeContainerLocation = Event.None;
        this.moveViewContainerToLocation = unsupported;
        this.getViewContainerBadgeEnablementState = unsupported;
        this.setViewContainerBadgeEnablementState = unsupported;
        this.getViewDescriptorById = () => null;
        this.getViewContainerByViewId = () => null;
        this.getDefaultContainerById = () => null;
        this.getViewLocationById = () => null;
        this.onDidChangeContainer = Event.None;
        this.moveViewsToContainer = unsupported;
        this.onDidChangeLocation = Event.None;
        this.moveViewToLocation = () => null;
        this.reset = () => null;
    }
}, 0 );
registerSingleton(IHistoryService, class HistoryService {
    constructor() {
        this.suspendTracking = () => ({
            dispose() {
            }
        });
        this.goForward = unsupported;
        this.goBack = unsupported;
        this.goPrevious = unsupported;
        this.goLast = unsupported;
        this.reopenLastClosedEditor = unsupported;
        this.getHistory = () => [];
        this.removeFromHistory = unsupported;
        this.getLastActiveWorkspaceRoot = () => undefined;
        this.getLastActiveFile = () => undefined;
        this.openNextRecentlyUsedEditor = unsupported;
        this.openPreviouslyUsedEditor = unsupported;
        this.clear = unsupported;
        this.clearRecentlyOpened = unsupported;
    }
}, 0 );
registerSingleton(ITaskService, class TaskService {
    constructor() {
        this.onDidChangeTaskConfig = Event.None;
        this.onDidStateChange = Event.None;
        this.supportsMultipleTaskExecutions = false;
        this.configureAction = unsupported;
        this.run = unsupported;
        this.inTerminal = () => false;
        this.getActiveTasks = async () => [];
        this.getBusyTasks = unsupported;
        this.terminate = unsupported;
        this.tasks = unsupported;
        this.taskTypes = unsupported;
        this.getWorkspaceTasks = unsupported;
        this.getSavedTasks = unsupported;
        this.removeRecentlyUsedTask = unsupported;
        this.getTask = unsupported;
        this.tryResolveTask = unsupported;
        this.createSorter = unsupported;
        this.getTaskDescription = unsupported;
        this.customize = unsupported;
        this.openConfig = unsupported;
        this.registerTaskProvider = unsupported;
        this.registerTaskSystem = () => { };
        this.onDidChangeTaskSystemInfo = Event.None;
        this.hasTaskSystemInfo = false;
        this.registerSupportedExecutions = () => { };
        this.extensionCallbackTaskComplete = unsupported;
        this.isReconnected = false;
        this.onDidReconnectToTasks = Event.None;
    }
}, 0 );
registerSingleton(IConfigurationResolverService, class ConfigurationResolverService {
    constructor() {
        this.resolveWithEnvironment = unsupported;
        this.resolveAsync = unsupported;
        this.resolveAnyAsync = unsupported;
        this.resolveAnyMap = unsupported;
        this.resolveWithInteractionReplace = unsupported;
        this.resolveWithInteraction = unsupported;
        this.contributeVariable = unsupported;
    }
}, 0 );
registerSingleton(IRemoteAgentService, class RemoteAgentService {
    constructor() {
        this.getConnection = () => null;
        this.getEnvironment = async () => null;
        this.getRawEnvironment = async () => null;
        this.getExtensionHostExitInfo = async () => null;
        this.getRoundTripTime = async () => undefined;
        this.whenExtensionsReady = async () => undefined;
        this.scanExtensions = async () => [];
        this.scanSingleExtension = async () => null;
        this.getDiagnosticInfo = async () => undefined;
        this.updateTelemetryLevel = async () => undefined;
        this.logTelemetry = async () => undefined;
        this.flushTelemetry = async () => undefined;
    }
}, 0 );
registerSingleton(ICustomEndpointTelemetryService, NullEndpointTelemetryService, 0 );
let MonacoSearchService = class MonacoSearchService {
    constructor(modelService) {
        this.modelService = modelService;
        this.aiTextSearch = unsupported;
        this.textSearchSplitSyncAsync = unsupported;
        this.registerSearchResultProvider = unsupported;
    }
    async textSearch() {
        return {
            results: [],
            messages: []
        };
    }
    async fileSearch() {
        return {
            results: ( this.modelService.getModels().map(model => ({
                resource: model.uri
            }))),
            messages: []
        };
    }
    async clearCache() {
    }
};
MonacoSearchService = __decorate([
    ( __param(0, IModelService))
], MonacoSearchService);
registerSingleton(ISearchService, MonacoSearchService, 0 );
registerSingleton(IEditSessionIdentityService, class EditSessionIdentityService {
    constructor() {
        this.registerEditSessionIdentityProvider = () => Disposable.None;
        this.getEditSessionIdentifier = async () => undefined;
        this.provideEditSessionIdentityMatch = async () => undefined;
        this.addEditSessionIdentityCreateParticipant = () => Disposable.None;
        this.onWillCreateEditSessionIdentity = async () => { };
    }
}, 0 );
registerSingleton(IWorkspaceEditingService, class WorkspaceEditingService {
    constructor() {
        this.addFolders = unsupported;
        this.removeFolders = unsupported;
        this.updateFolders = unsupported;
        this.enterWorkspace = unsupported;
        this.createAndEnterWorkspace = unsupported;
        this.saveAndEnterWorkspace = unsupported;
        this.copyWorkspaceSettings = unsupported;
        this.pickNewWorkspacePath = unsupported;
    }
}, 0 );
registerSingleton(ITimerService, class TimerService {
    constructor() {
        this.whenReady = unsupported;
        this.setPerformanceMarks = () => { };
        this.getPerformanceMarks = unsupported;
        this.getDuration = unsupported;
    }
    get perfBaseline() { return unsupported(); }
    get startupMetrics() { return unsupported(); }
}, 0 );
registerSingleton(IExtensionsWorkbenchService, class ExtensionsWorkbenchService {
    constructor() {
        this.getResourceExtensions = unsupported;
        this.updateRunningExtensions = unsupported;
        this.togglePreRelease = unsupported;
        this.isAutoUpdateEnabledFor = unsupported;
        this.updateAutoUpdateEnablementFor = unsupported;
        this.isAutoUpdateEnabled = unsupported;
        this.getAutoUpdateValue = unsupported;
        this.updateAll = unsupported;
        this.toggleApplyExtensionToAllProfiles = unsupported;
        this.whenInitialized = Promise.resolve();
        this.onChange = Event.None;
        this.onReset = Event.None;
        this.preferPreReleases = false;
        this.local = [];
        this.installed = [];
        this.outdated = [];
        this.queryLocal = unsupported;
        this.queryGallery = unsupported;
        this.getExtensions = unsupported;
        this.canInstall = unsupported;
        this.install = unsupported;
        this.installInServer = unsupported;
        this.uninstall = unsupported;
        this.installVersion = unsupported;
        this.reinstall = unsupported;
        this.canSetLanguage = unsupported;
        this.setLanguage = unsupported;
        this.setEnablement = unsupported;
        this.pinExtension = unsupported;
        this.open = unsupported;
        this.checkForUpdates = unsupported;
        this.getExtensionStatus = unsupported;
        this.isExtensionIgnoredToSync = unsupported;
        this.toggleExtensionIgnoredToSync = unsupported;
    }
}, 0 );
registerSingleton(IExtensionManagementServerService, class ExtensionManagementServerService {
    constructor() {
        this._serviceBrand = undefined;
        this.localExtensionManagementServer = null;
        this.remoteExtensionManagementServer = null;
        this.webExtensionManagementServer = null;
        this.getExtensionManagementServer = unsupported;
        this.getExtensionInstallLocation = unsupported;
    }
}, 0 );
registerSingleton(IExtensionRecommendationsService, class ExtensionRecommendationsService {
    constructor() {
        this.onDidChangeRecommendations = Event.None;
        this.getAllRecommendationsWithReason = () => ({});
        this.getImportantRecommendations = async () => [];
        this.getOtherRecommendations = async () => [];
        this.getFileBasedRecommendations = () => [];
        this.getExeBasedRecommendations = async () => ({ important: [], others: [] });
        this.getConfigBasedRecommendations = async () => ({ important: [], others: [] });
        this.getWorkspaceRecommendations = async () => [];
        this.getKeymapRecommendations = () => [];
        this.getLanguageRecommendations = () => [];
        this.getRemoteRecommendations = () => [];
    }
}, 0 );
registerSingleton(IUserDataAutoSyncService, class UserDataAutoSyncService {
    constructor() {
        this.onError = Event.None;
        this.turnOn = unsupported;
        this.turnOff = unsupported;
        this.triggerSync = unsupported;
    }
}, 0 );
registerSingleton(IIgnoredExtensionsManagementService, class IgnoredExtensionsManagementService {
    constructor() {
        this.getIgnoredExtensions = () => [];
        this.hasToNeverSyncExtension = () => false;
        this.hasToAlwaysSyncExtension = () => false;
        this.updateIgnoredExtensions = unsupported;
        this.updateSynchronizedExtensions = unsupported;
    }
}, 0 );
registerSingleton(IExtensionRecommendationNotificationService, class ExtensionRecommendationNotificationService {
    constructor() {
        this.ignoredRecommendations = [];
        this.hasToIgnoreRecommendationNotifications = () => false;
        this.promptImportantExtensionsInstallNotification = unsupported;
        this.promptWorkspaceRecommendations = unsupported;
    }
}, 0 );
registerSingleton(IWebExtensionsScannerService, class WebExtensionsScannerService {
    constructor() {
        this.scanSystemExtensions = async () => [];
        this.scanUserExtensions = async () => [];
        this.scanExtensionsUnderDevelopment = async () => [];
        this.scanExistingExtension = async () => null;
        this.addExtension = unsupported;
        this.addExtensionFromGallery = unsupported;
        this.removeExtension = async () => { };
        this.copyExtensions = async () => { };
        this.updateMetadata = unsupported;
        this.scanExtensionManifest = async () => null;
    }
}, 0 );
registerSingleton(IExtensionsScannerService, class ExtensionsScannerService {
    constructor() {
        this.onDidChangeCache = Event.None;
        this.getTargetPlatform = unsupported;
        this.scanAllExtensions = unsupported;
        this.scanSystemExtensions = unsupported;
        this.scanUserExtensions = unsupported;
        this.scanExtensionsUnderDevelopment = unsupported;
        this.scanExistingExtension = unsupported;
        this.scanOneOrMultipleExtensions = unsupported;
        this.scanMultipleExtensions = unsupported;
        this.scanMetadata = unsupported;
        this.updateMetadata = unsupported;
        this.initializeDefaultProfileExtensions = unsupported;
    }
    get systemExtensionsLocation() {
        return unsupported();
    }
    get userExtensionsLocation() {
        return unsupported();
    }
}, 0 );
registerSingleton(IExtensionsProfileScannerService, class ExtensionsProfileScannerService {
    constructor() {
        this.onAddExtensions = Event.None;
        this.onDidAddExtensions = Event.None;
        this.onRemoveExtensions = Event.None;
        this.onDidRemoveExtensions = Event.None;
        this.scanProfileExtensions = unsupported;
        this.addExtensionsToProfile = unsupported;
        this.updateMetadata = unsupported;
        this.removeExtensionFromProfile = unsupported;
    }
}, 0 );
registerSingleton(IExtensionIgnoredRecommendationsService, class ExtensionIgnoredRecommendationsService {
    constructor() {
        this.onDidChangeIgnoredRecommendations = Event.None;
        this.ignoredRecommendations = [];
        this.onDidChangeGlobalIgnoredRecommendation = Event.None;
        this.globalIgnoredRecommendations = [];
        this.toggleGlobalIgnoredRecommendation = unsupported;
    }
}, 0 );
registerSingleton(IWorkspaceExtensionsConfigService, class WorkspaceExtensionsConfigService {
    constructor() {
        this.onDidChangeExtensionsConfigs = Event.None;
        this.getExtensionsConfigs = unsupported;
        this.getRecommendations = unsupported;
        this.getUnwantedRecommendations = unsupported;
        this.toggleRecommendation = unsupported;
        this.toggleUnwantedRecommendation = unsupported;
    }
}, 0 );
registerSingleton(IWorkbenchExtensionEnablementService, class WorkbenchExtensionEnablementService {
    constructor() {
        this.getEnablementStates = (extensions) => ( extensions.map(() => 8 ));
        this.onEnablementChanged = Event.None;
        this.getEnablementState = () => 8 ;
        this.getDependenciesEnablementStates = () => [];
        this.canChangeEnablement = () => false;
        this.canChangeWorkspaceEnablement = () => false;
        this.isEnabled = () => true;
        this.isEnabledEnablementState = () => true;
        this.isDisabledGlobally = () => false;
        this.setEnablement = unsupported;
        this.updateExtensionsEnablementsWhenWorkspaceTrustChanges = unsupported;
    }
}, 0 );
registerSingleton(ITunnelService, class TunnelService {
    constructor() {
        this.canChangeProtocol = false;
        this.tunnels = Promise.resolve([]);
        this.canChangePrivacy = false;
        this.privacyOptions = [];
        this.onTunnelOpened = Event.None;
        this.onTunnelClosed = Event.None;
        this.canElevate = false;
        this.hasTunnelProvider = false;
        this.onAddedTunnelProvider = Event.None;
        this.canTunnel = () => false;
        this.openTunnel = unsupported;
        this.getExistingTunnel = async () => undefined;
        this.setEnvironmentTunnel = unsupported;
        this.closeTunnel = unsupported;
        this.setTunnelProvider = unsupported;
        this.setTunnelFeatures = unsupported;
        this.isPortPrivileged = () => false;
    }
}, 0 );
registerSingleton(IFilesConfigurationService, class FilesConfigurationService {
    constructor() {
        this.onDidChangeAutoSaveConfiguration = Event.None;
        this.onDidChangeAutoSaveDisabled = Event.None;
        this.hasShortAutoSaveDelay = () => false;
        this.disableAutoSave = unsupported;
        this.onDidChangeReadonly = Event.None;
        this.onDidChangeFilesAssociation = Event.None;
        this.onAutoSaveConfigurationChange = Event.None;
        this.getAutoSaveConfiguration = unsupported;
        this.getAutoSaveMode = unsupported;
        this.toggleAutoSave = unsupported;
        this.onReadonlyChange = Event.None;
        this.isReadonly = unsupported;
        this.updateReadonly = unsupported;
        this.onFilesAssociationChange = Event.None;
        this.isHotExitEnabled = true;
        this.hotExitConfiguration = undefined;
        this.preventSaveConflicts = unsupported;
    }
}, 0 );
registerSingleton(IUntitledTextEditorService, class UntitledTextEditorService {
    constructor() {
        this.onDidCreate = Event.None;
        this.canDispose = () => true;
        this.isUntitledWithAssociatedResource = () => false;
        this.onDidChangeDirty = Event.None;
        this.onDidChangeEncoding = Event.None;
        this.onDidChangeLabel = Event.None;
        this.onWillDispose = Event.None;
        this.create = unsupported;
        this.get = () => undefined;
        this.getValue = () => undefined;
        this.resolve = unsupported;
    }
}, 0 );
registerSingleton(IWorkingCopyBackupService, class WorkingCopyBackupService {
    async hasBackups() {
        return false;
    }
    hasBackupSync() {
        return false;
    }
    async getBackups() {
        return [];
    }
    async resolve() {
        return undefined;
    }
    async backup() {
    }
    async discardBackup() {
    }
    async discardBackups() {
    }
}, 0 );
registerSingleton(IWorkingCopyService, class WorkingCopyService {
    constructor() {
        this.onDidRegister = Event.None;
        this.onDidUnregister = Event.None;
        this.onDidChangeDirty = Event.None;
        this.onDidChangeContent = Event.None;
        this.onDidSave = Event.None;
        this.dirtyCount = 0;
        this.dirtyWorkingCopies = [];
        this.modifiedCount = 0;
        this.modifiedWorkingCopies = [];
        this.hasDirty = false;
        this.isDirty = () => false;
        this.workingCopies = [];
        this.has = () => false;
        this.get = () => undefined;
        this.getAll = () => undefined;
    }
    registerWorkingCopy() {
        return Disposable.None;
    }
}, 0 );
registerSingleton(IDecorationsService, class DecorationsService {
    constructor() {
        this.onDidChangeDecorations = Event.None;
        this.registerDecorationsProvider = unsupported;
        this.getDecoration = () => undefined;
    }
}, 0 );
registerSingleton(IElevatedFileService, class ElevatedFileService {
    constructor() {
        this.isSupported = () => false;
        this.writeFileElevated = unsupported;
    }
}, 0 );
registerSingleton(IFileDialogService, class FileDialogService {
    constructor() {
        this.preferredHome = unsupported;
        this.defaultFilePath = unsupported;
        this.defaultFolderPath = unsupported;
        this.defaultWorkspacePath = unsupported;
        this.pickFileFolderAndOpen = unsupported;
        this.pickFileAndOpen = unsupported;
        this.pickFolderAndOpen = unsupported;
        this.pickWorkspaceAndOpen = unsupported;
        this.pickFileToSave = unsupported;
        this.showSaveDialog = unsupported;
        this.showSaveConfirm = unsupported;
        this.showOpenDialog = unsupported;
    }
}, 0 );
registerSingleton(IJSONEditingService, class JSONEditingService {
    constructor() {
        this.write = unsupported;
    }
}, 1 );
registerSingleton(IWorkspacesService, class WorkspacesService {
    constructor() {
        this.enterWorkspace = unsupported;
        this.createUntitledWorkspace = unsupported;
        this.deleteUntitledWorkspace = unsupported;
        this.getWorkspaceIdentifier = unsupported;
        this.onDidChangeRecentlyOpened = Event.None;
        this.addRecentlyOpened = unsupported;
        this.removeRecentlyOpened = unsupported;
        this.clearRecentlyOpened = unsupported;
        this.getRecentlyOpened = unsupported;
        this.getDirtyWorkspaces = unsupported;
    }
}, 1 );
registerSingleton(ITextEditorService, class TextEditorService {
    constructor() {
        this.createTextEditor = unsupported;
        this.resolveTextEditor = unsupported;
    }
}, 0 );
registerSingleton(IEditorResolverService, class EditorResolverService {
    constructor() {
        this.getAllUserAssociations = unsupported;
        this.getAssociationsForResource = unsupported;
        this.updateUserAssociations = unsupported;
        this.onDidChangeEditorRegistrations = Event.None;
        this.bufferChangeEvents = unsupported;
        this.resolveEditor = unsupported;
        this.getEditors = () => ([]);
    }
    registerEditor() {
        return {
            dispose: () => { }
        };
    }
}, 0 );
registerSingleton(IOutputService, class OutputService {
    constructor() {
        this.onActiveOutputChannel = Event.None;
    }
    getChannel() {
        return undefined;
    }
    getChannelDescriptor() {
        return undefined;
    }
    getChannelDescriptors() {
        return [];
    }
    getActiveChannel() {
        return undefined;
    }
    async showChannel() {
    }
}, 1 );
registerSingleton(IOutputChannelModelService, class OutputChannelModelService {
    constructor() {
        this.createOutputChannelModel = unsupported;
    }
}, 1 );
registerSingleton(IExtensionResourceLoaderService, class ExtensionResourceLoaderService {
    constructor() {
        this.readExtensionResource = unsupported;
        this.supportsExtensionGalleryResources = false;
        this.isExtensionGalleryResource = () => false;
        this.getExtensionGalleryResourceURL = unsupported;
    }
}, 0 );
registerSingleton(IBuiltinExtensionsScannerService, class BuiltinExtensionsScannerService {
    scanBuiltinExtensions() {
        return Promise.resolve([]);
    }
}, 0 );
registerSingleton(IExplorerService, class ExplorerService {
    constructor() {
        this.roots = [];
        this.getContext = unsupported;
        this.hasViewFocus = unsupported;
        this.setEditable = unsupported;
        this.getEditable = unsupported;
        this.getEditableData = unsupported;
        this.isEditable = unsupported;
        this.findClosest = unsupported;
        this.findClosestRoot = unsupported;
        this.refresh = unsupported;
        this.setToCopy = unsupported;
        this.isCut = unsupported;
        this.applyBulkEdit = unsupported;
        this.select = unsupported;
        this.registerView = unsupported;
    }
    get sortOrderConfiguration() { return unsupported(); }
}, 1 );
registerSingleton(IExtensionStorageService, class ExtensionStorageService {
    constructor() {
        this.getExtensionState = () => undefined;
        this.getExtensionStateRaw = () => undefined;
        this.setExtensionState = unsupported;
        this.onDidChangeExtensionStorageToSync = Event.None;
        this.setKeysForSync = unsupported;
        this.getKeysForSync = () => undefined;
        this.addToMigrationList = unsupported;
        this.getSourceExtensionToMigrate = () => undefined;
    }
}, 1 );
registerSingleton(IGlobalExtensionEnablementService, class GlobalExtensionEnablementService {
    constructor() {
        this.onDidChangeEnablement = Event.None;
    }
    getDisabledExtensions() {
        return [];
    }
    enableExtension() {
        return Promise.resolve(true);
    }
    disableExtension() {
        return Promise.resolve(true);
    }
}, 1 );
registerSingleton(ILanguagePackService, class LanguagePackService {
    async getAvailableLanguages() {
        return [];
    }
    async getInstalledLanguages() {
        return [];
    }
    async getBuiltInExtensionTranslationsUri(id, language) {
        const uri = getBuiltInExtensionTranslationsUris(language)?.[id];
        return uri != null ? ( URI.parse(uri)) : undefined;
    }
}, 1 );
registerSingleton(ITreeViewsDnDService, class TreeViewsDnDService {
    constructor() {
        this.removeDragOperationTransfer = unsupported;
        this.addDragOperationTransfer = unsupported;
    }
}, 1 );
registerSingleton(ITreeViewsService, class TreeviewsService {
    constructor() {
        this.getRenderedTreeElement = unsupported;
        this.addRenderedTreeItemElement = unsupported;
        this.removeRenderedTreeItemElement = unsupported;
    }
}, 1 );
registerSingleton(IBreadcrumbsService, class BreadcrumbsService {
    constructor() {
        this.register = unsupported;
        this.getWidget = () => undefined;
    }
}, 0 );
registerSingleton(IOutlineService, class OutlineService {
    constructor() {
        this.onDidChange = Event.None;
        this.canCreateOutline = () => false;
        this.createOutline = async () => undefined;
        this.registerOutlineCreator = unsupported;
    }
}, 0 );
registerSingleton(IUpdateService, class UpdateService {
    constructor() {
        this.onStateChange = Event.None;
        this.state = State.Uninitialized;
        this.checkForUpdates = unsupported;
        this.downloadUpdate = unsupported;
        this.applyUpdate = unsupported;
        this.quitAndInstall = unsupported;
        this.isLatestVersion = async () => true;
        this._applySpecificUpdate = unsupported;
    }
}, 0 );
registerSingleton(IStatusbarService, class StatusbarService {
    constructor() {
        this.getPart = unsupported;
        this.createAuxiliaryStatusbarPart = unsupported;
        this.createScoped = unsupported;
        this.dispose = unsupported;
        this.onDidChangeEntryVisibility = Event.None;
        this.addEntry = () => ({
            dispose: () => { },
            update: () => { }
        });
        this.isEntryVisible = () => false;
        this.updateEntryVisibility = () => { };
        this.focus = () => { };
        this.focusNextEntry = () => { };
        this.focusPreviousEntry = () => { };
        this.isEntryFocused = () => false;
        this.overrideStyle = () => Disposable.None;
    }
}, 0 );
registerSingleton(IExtensionGalleryService, class ExtensionGalleryService {
    constructor() {
        this.isEnabled = () => false;
        this.query = unsupported;
        this.getExtensions = unsupported;
        this.isExtensionCompatible = unsupported;
        this.getCompatibleExtension = unsupported;
        this.getAllCompatibleVersions = unsupported;
        this.download = unsupported;
        this.downloadSignatureArchive = unsupported;
        this.reportStatistic = unsupported;
        this.getReadme = unsupported;
        this.getManifest = unsupported;
        this.getChangelog = unsupported;
        this.getCoreTranslation = unsupported;
        this.getExtensionsControlManifest = unsupported;
    }
}, 0 );
registerSingleton(ITerminalService, class TerminalService {
    constructor() {
        this.onAnyInstanceData = Event.None;
        this.moveIntoNewEditor = unsupported;
        this.detachedInstances = [];
        this.onAnyInstanceDataInput = Event.None;
        this.onAnyInstanceIconChange = Event.None;
        this.onAnyInstanceMaximumDimensionsChange = Event.None;
        this.onAnyInstancePrimaryStatusChange = Event.None;
        this.onAnyInstanceProcessIdReady = Event.None;
        this.onAnyInstanceSelectionChange = Event.None;
        this.onAnyInstanceTitleChange = Event.None;
        this.createDetachedTerminal = unsupported;
        this.onDidChangeSelection = Event.None;
        this.detachedXterms = [];
        this.restoredGroupCount = 0;
        this.createDetachedXterm = unsupported;
        this.instances = [];
        this.revealActiveTerminal = unsupported;
        this.isProcessSupportRegistered = false;
        this.connectionState = 1 ;
        this.defaultLocation = TerminalLocation.Panel;
        this.onDidChangeActiveGroup = Event.None;
        this.onDidDisposeGroup = Event.None;
        this.onDidCreateInstance = Event.None;
        this.onDidReceiveProcessId = Event.None;
        this.onDidChangeInstanceDimensions = Event.None;
        this.onDidMaximumDimensionsChange = Event.None;
        this.onDidRequestStartExtensionTerminal = Event.None;
        this.onDidChangeInstanceTitle = Event.None;
        this.onDidChangeInstanceIcon = Event.None;
        this.onDidChangeInstanceColor = Event.None;
        this.onDidChangeInstancePrimaryStatus = Event.None;
        this.onDidInputInstanceData = Event.None;
        this.onDidRegisterProcessSupport = Event.None;
        this.onDidChangeConnectionState = Event.None;
        this.createTerminal = unsupported;
        this.getInstanceFromId = unsupported;
        this.getInstanceFromIndex = unsupported;
        this.getReconnectedTerminals = unsupported;
        this.getActiveOrCreateInstance = unsupported;
        this.moveToEditor = unsupported;
        this.moveToTerminalView = unsupported;
        this.getPrimaryBackend = unsupported;
        this.refreshActiveGroup = unsupported;
        this.registerProcessSupport = () => { };
        this.showProfileQuickPick = unsupported;
        this.setContainers = unsupported;
        this.requestStartExtensionTerminal = unsupported;
        this.isAttachedToTerminal = unsupported;
        this.getEditableData = unsupported;
        this.setEditable = unsupported;
        this.isEditable = unsupported;
        this.safeDisposeTerminal = unsupported;
        this.getDefaultInstanceHost = unsupported;
        this.getInstanceHost = unsupported;
        this.resolveLocation = unsupported;
        this.setNativeDelegate = unsupported;
        this.toggleEscapeSequenceLogging = unsupported;
        this.getEditingTerminal = unsupported;
        this.setEditingTerminal = unsupported;
        this.activeInstance = undefined;
        this.onDidDisposeInstance = Event.None;
        this.onDidFocusInstance = Event.None;
        this.onDidChangeActiveInstance = Event.None;
        this.onDidChangeInstances = Event.None;
        this.onDidChangeInstanceCapability = Event.None;
        this.setActiveInstance = unsupported;
        this.focusActiveInstance = unsupported;
        this.getInstanceFromResource = unsupported;
    }
    createOnInstanceCapabilityEvent() {
        return {
            event: Event.None,
            dispose() { }
        };
    }
    createOnInstanceEvent(getEvent) {
        return new DynamicListEventMultiplexer(this.instances, this.onDidCreateInstance, this.onDidDisposeInstance, getEvent);
    }
    get whenConnected() {
        return (async () => {
            unsupported();
        })();
    }
    get configHelper() {
        return unsupported();
    }
}, 1 );
registerSingleton(ITerminalConfigurationService, class TerminalConfigurationService {
    constructor() {
        this.onConfigChanged = Event.None;
        this.setPanelContainer = unsupported;
        this.configFontIsMonospace = unsupported;
        this.getFont = unsupported;
    }
    get config() {
        return unsupported();
    }
}, 1 );
registerSingleton(ITerminalEditorService, class TerminalEditorService {
    constructor() {
        this.instances = [];
        this.openEditor = unsupported;
        this.detachActiveEditorInstance = unsupported;
        this.detachInstance = unsupported;
        this.splitInstance = unsupported;
        this.revealActiveEditor = unsupported;
        this.resolveResource = unsupported;
        this.reviveInput = unsupported;
        this.getInputFromResource = unsupported;
        this.activeInstance = undefined;
        this.onDidDisposeInstance = Event.None;
        this.onDidFocusInstance = Event.None;
        this.onDidChangeActiveInstance = Event.None;
        this.onDidChangeInstances = Event.None;
        this.onDidChangeInstanceCapability = Event.None;
        this.setActiveInstance = unsupported;
        this.focusActiveInstance = unsupported;
        this.getInstanceFromResource = unsupported;
    }
}, 1 );
registerSingleton(ITerminalGroupService, class TerminalGroupService {
    constructor() {
        this.lastAccessedMenu = 'inline-tab';
        this.instances = [];
        this.groups = [];
        this.activeGroup = undefined;
        this.activeGroupIndex = 0;
        this.onDidChangeActiveGroup = Event.None;
        this.onDidDisposeGroup = Event.None;
        this.onDidChangeGroups = Event.None;
        this.onDidShow = Event.None;
        this.onDidChangePanelOrientation = Event.None;
        this.createGroup = unsupported;
        this.getGroupForInstance = unsupported;
        this.moveGroup = unsupported;
        this.moveGroupToEnd = unsupported;
        this.moveInstance = unsupported;
        this.unsplitInstance = unsupported;
        this.joinInstances = unsupported;
        this.instanceIsSplit = unsupported;
        this.getGroupLabels = unsupported;
        this.setActiveGroupByIndex = unsupported;
        this.setActiveGroupToNext = unsupported;
        this.setActiveGroupToPrevious = unsupported;
        this.setActiveInstanceByIndex = unsupported;
        this.setContainer = unsupported;
        this.showPanel = unsupported;
        this.hidePanel = unsupported;
        this.focusTabs = unsupported;
        this.focusHover = unsupported;
        this.showTabs = unsupported;
        this.updateVisibility = unsupported;
        this.onDidDisposeInstance = Event.None;
        this.onDidFocusInstance = Event.None;
        this.onDidChangeActiveInstance = Event.None;
        this.onDidChangeInstances = Event.None;
        this.onDidChangeInstanceCapability = Event.None;
        this.setActiveInstance = unsupported;
        this.focusActiveInstance = unsupported;
        this.getInstanceFromResource = unsupported;
    }
}, 1 );
registerSingleton(ITerminalInstanceService, class TerminalInstanceService {
    constructor() {
        this.getRegisteredBackends = () => ( [].values());
        this.onDidCreateInstance = Event.None;
        this.convertProfileToShellLaunchConfig = unsupported;
        this.createInstance = unsupported;
        this.getBackend = unsupported;
        this.didRegisterBackend = unsupported;
    }
}, 1 );
registerSingleton(ITerminalProfileService, class TerminalProfileService {
    constructor() {
        this.availableProfiles = [];
        this.contributedProfiles = [];
        this.profilesReady = Promise.resolve();
        this.getPlatformKey = unsupported;
        this.refreshAvailableProfiles = unsupported;
        this.getDefaultProfileName = () => undefined;
        this.getDefaultProfile = () => undefined;
        this.onDidChangeAvailableProfiles = Event.None;
        this.getContributedDefaultProfile = unsupported;
        this.registerContributedProfile = unsupported;
        this.getContributedProfileProvider = unsupported;
        this.registerTerminalProfileProvider = unsupported;
    }
}, 1 );
registerSingleton(ITerminalLogService, class TerminalLogService {
    constructor() {
        this.onDidChangeLogLevel = Event.None;
        this.getLevel = unsupported;
        this.setLevel = unsupported;
        this.trace = unsupported;
        this.debug = unsupported;
        this.info = unsupported;
        this.warn = unsupported;
        this.error = unsupported;
        this.flush = unsupported;
        this.dispose = unsupported;
    }
}, 1 );
registerSingleton(ITerminalLinkProviderService, class TerminalLinkProviderService {
    constructor() {
        this.linkProviders = new Set([]);
        this.onDidAddLinkProvider = Event.None;
        this.onDidRemoveLinkProvider = Event.None;
        this.registerLinkProvider = unsupported;
    }
}, 1 );
registerSingleton(ITerminalContributionService, class TerminalContributionService {
    constructor() {
        this.terminalProfiles = [];
    }
}, 1 );
registerSingleton(ITerminalProfileResolverService, class TerminalProfileResolverService {
    constructor() {
        this.resolveIcon = unsupported;
        this.resolveShellLaunchConfig = unsupported;
        this.getDefaultProfile = async () => ({
            profileName: 'bash',
            path: '/bin/bash',
            isDefault: true
        });
        this.getDefaultShell = unsupported;
        this.getDefaultShellArgs = unsupported;
        this.getDefaultIcon = unsupported;
        this.getEnvironment = unsupported;
        this.createProfileFromShellAndShellArgs = unsupported;
    }
}, 1 );
registerSingleton(IEnvironmentVariableService, class EnvironmentVariableService {
    constructor() {
        this.collections = new Map();
        this.onDidChangeCollections = Event.None;
        this.set = unsupported;
        this.delete = unsupported;
    }
    get mergedCollection() {
        return unsupported();
    }
}, 1 );
registerSingleton(ITerminalQuickFixService, class TerminalQuickFixService {
    constructor() {
        this.onDidRegisterProvider = Event.None;
        this.onDidRegisterCommandSelector = Event.None;
        this.onDidUnregisterProvider = Event.None;
        this.extensionQuickFixes = Promise.resolve([]);
        this.providers = new Map();
        this.registerQuickFixProvider = unsupported;
        this.registerCommandSelector = unsupported;
    }
}, 1 );
registerSingleton(IExtensionManagementService, class ExtensionManagementService {
    constructor() {
        this.toggleAppliationScope = async (extension) => extension;
        this.installGalleryExtensions = unsupported;
        this.onInstallExtension = Event.None;
        this.onDidInstallExtensions = Event.None;
        this.onUninstallExtension = Event.None;
        this.onDidUninstallExtension = Event.None;
        this.onDidUpdateExtensionMetadata = Event.None;
        this.zip = unsupported;
        this.unzip = unsupported;
        this.getManifest = unsupported;
        this.install = unsupported;
        this.canInstall = unsupported;
        this.installFromGallery = unsupported;
        this.installFromLocation = unsupported;
        this.installExtensionsFromProfile = unsupported;
        this.uninstall = unsupported;
        this.reinstallFromGallery = unsupported;
        this.getInstalled = async () => [];
        this.getExtensionsControlManifest = unsupported;
        this.copyExtensions = unsupported;
        this.updateMetadata = unsupported;
        this.download = unsupported;
        this.registerParticipant = unsupported;
        this.getTargetPlatform = unsupported;
        this.cleanUp = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncWorkbenchService, class UserDataSyncWorkbenchService {
    constructor() {
        this.enabled = false;
        this.authenticationProviders = [];
        this.all = [];
        this.current = undefined;
        this.accountStatus = "unavailable" ;
        this.onDidChangeAccountStatus = Event.None;
        this.turnOn = unsupported;
        this.turnoff = unsupported;
        this.signIn = unsupported;
        this.resetSyncedData = unsupported;
        this.showSyncActivity = unsupported;
        this.syncNow = unsupported;
        this.synchroniseUserDataSyncStoreType = unsupported;
        this.showConflicts = unsupported;
        this.accept = unsupported;
        this.getAllLogResources = unsupported;
        this.downloadSyncActivity = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncEnablementService, class UserDataSyncEnablementService {
    constructor() {
        this.onDidChangeEnablement = Event.None;
        this.isEnabled = () => false;
        this.canToggleEnablement = () => false;
        this.setEnablement = unsupported;
        this.onDidChangeResourceEnablement = Event.None;
        this.isResourceEnabled = () => false;
        this.setResourceEnablement = unsupported;
        this.getResourceSyncStateVersion = () => undefined;
    }
}, 1 );
registerSingleton(IKeybindingEditingService, class KeybindingEditingService {
    constructor() {
        this.addKeybinding = unsupported;
        this.editKeybinding = unsupported;
        this.removeKeybinding = unsupported;
        this.resetKeybinding = unsupported;
    }
}, 1 );
registerSingleton(IPreferencesSearchService, class PreferencesSearchService {
    constructor() {
        this.getLocalSearchProvider = unsupported;
        this.getRemoteSearchProvider = unsupported;
    }
}, 1 );
registerSingleton(INotebookService, class NotebookService {
    constructor() {
        this.canResolve = async () => false;
        this.onAddViewType = Event.None;
        this.onWillRemoveViewType = Event.None;
        this.onDidChangeOutputRenderers = Event.None;
        this.onWillAddNotebookDocument = Event.None;
        this.onDidAddNotebookDocument = Event.None;
        this.onWillRemoveNotebookDocument = Event.None;
        this.onDidRemoveNotebookDocument = Event.None;
        this.registerNotebookSerializer = unsupported;
        this.withNotebookDataProvider = unsupported;
        this.getOutputMimeTypeInfo = unsupported;
        this.getViewTypeProvider = () => undefined;
        this.getRendererInfo = () => undefined;
        this.getRenderers = () => [];
        this.getStaticPreloads = unsupported;
        this.updateMimePreferredRenderer = unsupported;
        this.saveMimeDisplayOrder = unsupported;
        this.createNotebookTextModel = unsupported;
        this.getNotebookTextModel = () => undefined;
        this.getNotebookTextModels = unsupported;
        this.listNotebookDocuments = () => [];
        this.registerContributedNotebookType = unsupported;
        this.getContributedNotebookType = unsupported;
        this.getContributedNotebookTypes = () => [];
        this.getNotebookProviderResourceRoots = () => [];
        this.setToCopy = unsupported;
        this.getToCopy = unsupported;
        this.clearEditorCache = unsupported;
    }
}, 1 );
registerSingleton(IReplaceService, class ReplaceService {
    constructor() {
        this.replace = unsupported;
        this.openReplacePreview = unsupported;
        this.updateReplacePreview = unsupported;
    }
}, 1 );
registerSingleton(ISearchHistoryService, class SearchHistoryService {
    constructor() {
        this.onDidClearHistory = Event.None;
        this.clearHistory = unsupported;
        this.load = unsupported;
        this.save = unsupported;
    }
}, 1 );
registerSingleton(INotebookEditorService, class NotebookEditorService {
    constructor() {
        this.retrieveWidget = unsupported;
        this.retrieveExistingWidgetFromURI = () => undefined;
        this.retrieveAllExistingWidgets = () => [];
        this.onDidAddNotebookEditor = Event.None;
        this.onDidRemoveNotebookEditor = Event.None;
        this.addNotebookEditor = unsupported;
        this.removeNotebookEditor = unsupported;
        this.getNotebookEditor = () => undefined;
        this.listNotebookEditors = () => [];
    }
}, 1 );
registerSingleton(ISearchViewModelWorkbenchService, class SearchWorkbenchService {
    get searchModel() {
        return unsupported();
    }
}, 1 );
registerSingleton(INotebookEditorModelResolverService, class NotebookEditorModelResolverService {
    constructor() {
        this.onDidSaveNotebook = Event.None;
        this.onDidChangeDirty = Event.None;
        this.onWillFailWithConflict = Event.None;
        this.isDirty = unsupported;
        this.resolve = unsupported;
    }
}, 1 );
registerSingleton(IWorkingCopyEditorService, class WorkingCopyEditorService {
    constructor() {
        this.onDidRegisterHandler = Event.None;
        this.registerHandler = () => Disposable.None;
        this.findEditor = () => undefined;
    }
}, 1 );
registerSingleton(IUserActivityService, class UserActivityService {
    constructor() {
        this.isActive = false;
        this.onDidChangeIsActive = Event.None;
        this.markActive = unsupported;
    }
}, 1 );
registerSingleton(ICanonicalUriService, class CanonicalUriService {
    constructor() {
        this.registerCanonicalUriProvider = unsupported;
    }
}, 1 );
registerSingleton(IExtensionStatusBarItemService, class ExtensionStatusBarItemService {
    constructor() {
        this.onDidChange = Event.None;
    }
    setOrUpdateEntry() {
        return 1 ;
    }
    unsetEntry() {
    }
    getEntries() {
        return [];
    }
}, 1 );
registerSingleton(IWorkbenchAssignmentService, class WorkbenchAssignmentService {
    constructor() {
        this.getCurrentExperiments = async () => [];
        this.getTreatment = async () => undefined;
    }
}, 1 );
registerSingleton(IChatService, class ChatService {
    constructor() {
        this.isEnabled = () => false;
        this.resendRequest = unsupported;
        this.onDidUnregisterProvider = Event.None;
        this.clearAllHistoryEntries = unsupported;
        this.onDidSubmitAgent = Event.None;
        this.onDidRegisterProvider = Event.None;
        this.hasSessions = () => false;
        this.onDidDisposeSession = Event.None;
        this.transferredSessionData = undefined;
        this.onDidSubmitSlashCommand = Event.None;
        this.getSessionId = () => undefined;
        this.transferredSessionId = undefined;
        this.transferChatSession = unsupported;
        this.registerProvider = unsupported;
        this.registerSlashCommandProvider = unsupported;
        this.getProviderInfos = () => [];
        this.startSession = unsupported;
        this.getSession = () => undefined;
        this.getOrRestoreSession = () => undefined;
        this.loadSessionFromContent = () => undefined;
        this.sendRequest = unsupported;
        this.removeRequest = unsupported;
        this.cancelCurrentRequestForSession = unsupported;
        this.getSlashCommands = unsupported;
        this.clearSession = unsupported;
        this.addRequest = unsupported;
        this.addCompleteRequest = unsupported;
        this.sendRequestToProvider = unsupported;
        this.getHistory = () => [];
        this.removeHistoryEntry = unsupported;
        this.onDidPerformUserAction = Event.None;
        this.notifyUserAction = unsupported;
    }
}, 1 );
registerSingleton(ILanguageModelStatsService, class LanguageModelStatsService {
    constructor() {
        this.update = unsupported;
    }
}, 1 );
registerSingleton(IQuickChatService, class QuickChatService {
    constructor() {
        this.focused = false;
        this.onDidClose = Event.None;
        this.enabled = false;
        this.toggle = unsupported;
        this.focus = unsupported;
        this.open = unsupported;
        this.close = unsupported;
        this.openInChatView = unsupported;
    }
}, 1 );
registerSingleton(IChatAgentService, class QuickChatService {
    constructor() {
        this._serviceBrand = undefined;
        this.getContributedDefaultAgent = () => undefined;
        this.registerAgentImplementation = unsupported;
        this.registerDynamicAgent = unsupported;
        this.getActivatedAgents = () => [];
        this.getAgentsByName = () => [];
        this.getFollowups = unsupported;
        this.getDefaultAgent = unsupported;
        this.getSecondaryAgent = unsupported;
        this.updateAgent = unsupported;
        this.onDidChangeAgents = Event.None;
        this.registerAgentData = unsupported;
        this.registerAgentCallback = unsupported;
        this.registerAgent = unsupported;
        this.invokeAgent = unsupported;
        this.getAgents = unsupported;
        this.getAgent = unsupported;
        this.hasAgent = unsupported;
    }
}, 1 );
registerSingleton(IEmbedderTerminalService, class EmbedderTerminalService {
    constructor() {
        this.onDidCreateTerminal = Event.None;
        this.createTerminal = unsupported;
    }
}, 1 );
registerSingleton(ICustomEditorService, class CustomEditorService {
    constructor() {
        this.getCustomEditor = unsupported;
        this.getAllCustomEditors = unsupported;
        this.getContributedCustomEditors = unsupported;
        this.getUserConfiguredCustomEditors = unsupported;
        this.registerCustomEditorCapabilities = () => Disposable.None;
        this.getCustomEditorCapabilities = () => undefined;
    }
    get models() {
        return unsupported();
    }
}, 1 );
registerSingleton(IWebviewService, class WebviewService {
    constructor() {
        this.webviews = [];
        this.onDidChangeActiveWebview = Event.None;
        this.createWebviewElement = unsupported;
        this.createWebviewOverlay = unsupported;
    }
}, 1 );
registerSingleton(IWebviewViewService, class WebviewService {
    constructor() {
        this.onNewResolverRegistered = Event.None;
        this.register = unsupported;
        this.resolve = unsupported;
    }
}, 1 );
registerSingleton(ILocaleService, class LocaleService {
    constructor() {
        this.setLocale = unsupported;
    }
    clearLocalePreference() {
        return Promise.resolve();
    }
}, 1 );
registerSingleton(IWebviewWorkbenchService, class WebviewWorkbenchService {
    constructor() {
        this.onDidChangeActiveWebviewEditor = Event.None;
        this.openWebview = unsupported;
        this.openRevivedWebview = unsupported;
        this.revealWebview = unsupported;
        this.registerResolver = () => Disposable.None;
        this.shouldPersist = unsupported;
        this.resolveWebview = unsupported;
    }
    get iconManager() {
        return unsupported();
    }
}, 1 );
registerSingleton(IRemoteAuthorityResolverService, class RemoteAuthorityResolverService {
    constructor() {
        this.onDidChangeConnectionData = Event.None;
        this.resolveAuthority = unsupported;
        this.getConnectionData = unsupported;
        this.getCanonicalURI = unsupported;
        this._clearResolvedAuthority = unsupported;
        this._setResolvedAuthority = unsupported;
        this._setResolvedAuthorityError = unsupported;
        this._setAuthorityConnectionToken = unsupported;
        this._setCanonicalURIProvider = unsupported;
    }
}, 1 );
registerSingleton(IExternalUriOpenerService, class ExternalUriOpenerService {
    constructor() {
        this.registerExternalOpenerProvider = () => Disposable.None;
        this.getOpener = async () => undefined;
    }
}, 1 );
registerSingleton(IAccessibleViewService, class AccessibleViewService {
    constructor() {
        this.navigateToCodeBlock = unsupported;
        this.getCodeBlockContext = () => undefined;
        this.showLastProvider = unsupported;
        this.showAccessibleViewHelp = unsupported;
        this.goToSymbol = unsupported;
        this.disableHint = unsupported;
        this.next = unsupported;
        this.previous = unsupported;
        this.getOpenAriaHint = unsupported;
        this.show = unsupported;
        this.registerProvider = unsupported;
        this.getPosition = unsupported;
        this.setPosition = unsupported;
        this.getLastPosition = unsupported;
    }
}, 1 );
registerSingleton(IAccessibleViewInformationService, class AccessibleViewInformationService {
    constructor() {
        this.hasShownAccessibleView = () => false;
    }
}, 1 );
registerSingleton(IWorkbenchExtensionManagementService, class WorkbenchExtensionManagementService {
    constructor() {
        this.onDidEnableExtensions = Event.None;
        this.isWorkspaceExtensionsSupported = () => false;
        this.getExtensions = async () => [];
        this.getInstalledWorkspaceExtensions = async () => [];
        this.installResourceExtension = unsupported;
        this.toggleAppliationScope = async (extension) => extension;
        this.onInstallExtension = Event.None;
        this.onDidInstallExtensions = Event.None;
        this.onUninstallExtension = Event.None;
        this.onDidUninstallExtension = Event.None;
        this.onDidChangeProfile = Event.None;
        this.installVSIX = unsupported;
        this.installFromLocation = unsupported;
        this.updateFromGallery = unsupported;
        this.onDidUpdateExtensionMetadata = Event.None;
        this.zip = unsupported;
        this.unzip = unsupported;
        this.getManifest = unsupported;
        this.install = unsupported;
        this.canInstall = unsupported;
        this.installFromGallery = unsupported;
        this.installGalleryExtensions = unsupported;
        this.installExtensionsFromProfile = unsupported;
        this.uninstall = unsupported;
        this.reinstallFromGallery = unsupported;
        this.getInstalled = unsupported;
        this.getExtensionsControlManifest = unsupported;
        this.copyExtensions = unsupported;
        this.updateMetadata = unsupported;
        this.download = unsupported;
        this.registerParticipant = unsupported;
        this.getTargetPlatform = unsupported;
        this.cleanUp = unsupported;
    }
}, 1 );
registerSingleton(IExtensionManifestPropertiesService, class ExtensionManifestPropertiesService {
    constructor() {
        this.prefersExecuteOnUI = unsupported;
        this.prefersExecuteOnWorkspace = unsupported;
        this.prefersExecuteOnWeb = unsupported;
        this.canExecuteOnUI = unsupported;
        this.canExecuteOnWorkspace = unsupported;
        this.canExecuteOnWeb = unsupported;
        this.getExtensionKind = unsupported;
        this.getUserConfiguredExtensionKind = unsupported;
        this.getExtensionUntrustedWorkspaceSupportType = unsupported;
        this.getExtensionVirtualWorkspaceSupportType = unsupported;
    }
}, 1 );
registerSingleton(IWorkspaceTrustEnablementService, class WorkspaceTrustEnablementService {
    isWorkspaceTrustEnabled() {
        return false;
    }
}, 1 );
registerSingleton(IRemoteExtensionsScannerService, class RemoteExtensionsScannerService {
    whenExtensionsReady() {
        throw new Error('Method not implemented.');
    }
    async scanExtensions() {
        return [];
    }
    async scanSingleExtension() {
        return null;
    }
}, 1 );
registerSingleton(IURLService, class URLService {
    constructor() {
        this.create = unsupported;
        this.open = async () => false;
        this.registerHandler = unsupported;
    }
}, 1 );
registerSingleton(IRemoteSocketFactoryService, class RemoteSocketFactoryService {
    constructor() {
        this.register = unsupported;
        this.connect = unsupported;
    }
}, 1 );
registerSingleton(IQuickDiffService, class QuickDiffService {
    constructor() {
        this.onDidChangeQuickDiffProviders = Event.None;
        this.addQuickDiffProvider = unsupported;
        this.getQuickDiffs = unsupported;
    }
}, 1 );
registerSingleton(ISCMService, class SCMService {
    constructor() {
        this.onDidChangeInputValueProviders = Event.None;
        this.getDefaultInputValueProvider = unsupported;
        this.registerSCMInputValueProvider = unsupported;
        this.onDidAddRepository = Event.None;
        this.onDidRemoveRepository = Event.None;
        this.repositories = [];
        this.repositoryCount = 0;
        this.registerSCMProvider = unsupported;
        this.getRepository = unsupported;
    }
}, 1 );
registerSingleton(IDownloadService, class DownloadService {
    constructor() {
        this.download = unsupported;
    }
}, 1 );
registerSingleton(IExtensionUrlHandler, class ExtensionUrlHandler {
    constructor() {
        this.registerExtensionHandler = unsupported;
        this.unregisterExtensionHandler = unsupported;
    }
}, 1 );
registerSingleton(ICommentService, class CommentService {
    constructor() {
        this.resourceHasCommentingRanges = () => false;
        this.onDidChangeActiveEditingCommentThread = Event.None;
        this.setActiveEditingCommentThread = unsupported;
        this.setActiveCommentAndThread = unsupported;
        this.onDidSetResourceCommentInfos = Event.None;
        this.onDidSetAllCommentThreads = Event.None;
        this.onDidUpdateCommentThreads = Event.None;
        this.onDidUpdateNotebookCommentThreads = Event.None;
        this.onDidChangeActiveCommentThread = Event.None;
        this.onDidChangeCurrentCommentThread = Event.None;
        this.onDidUpdateCommentingRanges = Event.None;
        this.onDidChangeActiveCommentingRange = Event.None;
        this.onDidSetDataProvider = Event.None;
        this.onDidDeleteDataProvider = Event.None;
        this.onDidChangeCommentingEnabled = Event.None;
        this.isCommentingEnabled = false;
        this.setDocumentComments = unsupported;
        this.setWorkspaceComments = unsupported;
        this.removeWorkspaceComments = unsupported;
        this.registerCommentController = unsupported;
        this.unregisterCommentController = () => { };
        this.getCommentController = unsupported;
        this.createCommentThreadTemplate = unsupported;
        this.updateCommentThreadTemplate = unsupported;
        this.getCommentMenus = unsupported;
        this.updateComments = unsupported;
        this.updateNotebookComments = unsupported;
        this.disposeCommentThread = unsupported;
        this.getDocumentComments = async () => [];
        this.getNotebookComments = async () => [];
        this.updateCommentingRanges = unsupported;
        this.hasReactionHandler = unsupported;
        this.toggleReaction = unsupported;
        this.setActiveCommentThread = unsupported;
        this.setCurrentCommentThread = unsupported;
        this.enableCommenting = unsupported;
        this.registerContinueOnCommentProvider = unsupported;
        this.removeContinueOnComment = unsupported;
    }
    get commentsModel() {
        return unsupported();
    }
}, 1 );
registerSingleton(INotebookCellStatusBarService, class NotebookCellStatusBarService {
    constructor() {
        this.onDidChangeProviders = Event.None;
        this.onDidChangeItems = Event.None;
        this.registerCellStatusBarItemProvider = unsupported;
        this.getStatusBarItemsForCell = unsupported;
    }
}, 1 );
registerSingleton(INotebookKernelService, class NotebookKernelService {
    constructor() {
        this.onDidNotebookVariablesUpdate = Event.None;
        this.notifyVariablesChange = unsupported;
        this.onDidAddKernel = Event.None;
        this.onDidRemoveKernel = Event.None;
        this.onDidChangeSelectedNotebooks = Event.None;
        this.onDidChangeNotebookAffinity = Event.None;
        this.registerKernel = unsupported;
        this.getMatchingKernel = unsupported;
        this.getSelectedOrSuggestedKernel = unsupported;
        this.selectKernelForNotebook = unsupported;
        this.preselectKernelForNotebook = unsupported;
        this.updateKernelNotebookAffinity = unsupported;
        this.onDidChangeKernelDetectionTasks = Event.None;
        this.registerNotebookKernelDetectionTask = unsupported;
        this.getKernelDetectionTasks = unsupported;
        this.onDidChangeSourceActions = Event.None;
        this.getSourceActions = unsupported;
        this.getRunningSourceActions = unsupported;
        this.registerKernelSourceActionProvider = unsupported;
        this.getKernelSourceActions2 = unsupported;
    }
}, 1 );
registerSingleton(INotebookRendererMessagingService, class NotebookRendererMessagingService {
    constructor() {
        this.onShouldPostMessage = Event.None;
        this.prepare = unsupported;
        this.getScoped = unsupported;
        this.receiveMessage = unsupported;
    }
}, 1 );
registerSingleton(IInteractiveHistoryService, class InteractiveHistoryService {
    constructor() {
        this.addToHistory = unsupported;
        this.getPreviousValue = unsupported;
        this.getNextValue = unsupported;
        this.replaceLast = unsupported;
        this.clearHistory = unsupported;
        this.has = unsupported;
    }
}, 1 );
registerSingleton(IInteractiveDocumentService, class InteractiveDocumentService {
    constructor() {
        this.onWillAddInteractiveDocument = Event.None;
        this.onWillRemoveInteractiveDocument = Event.None;
        this.willCreateInteractiveDocument = unsupported;
        this.willRemoveInteractiveDocument = unsupported;
    }
}, 1 );
registerSingleton(IActiveLanguagePackService, class ActiveLanguagePackService {
    async getExtensionIdProvidingCurrentLocale() {
        return getExtensionIdProvidingCurrentLocale();
    }
}, 0 );
registerSingleton(IRemoteUserDataProfilesService, class RemoteUserDataProfilesService {
    constructor() {
        this.getRemoteProfiles = async () => [];
        this.getRemoteProfile = unsupported;
    }
}, 0 );
registerSingleton(IExtensionBisectService, class ExtensionBisectService {
    constructor() {
        this.isDisabledByBisect = () => false;
        this.isActive = false;
        this.disabledCount = 0;
        this.start = unsupported;
        this.next = unsupported;
        this.reset = unsupported;
    }
}, 0 );
registerSingleton(IUserDataSyncAccountService, class UserDataSyncAccountService {
    constructor() {
        this.onTokenFailed = Event.None;
        this.account = undefined;
        this.onDidChangeAccount = Event.None;
    }
    updateAccount() {
        return Promise.resolve();
    }
}, 0 );
registerSingleton(IInlineChatService, class InlineChatService {
    constructor() {
        this.onDidChangeProviders = Event.None;
        this.addProvider = unsupported;
        this.getAllProvider = () => [];
    }
}, 1 );
registerSingleton(IChatWidgetService, class ChatWidgetService {
    constructor() {
        this.getWidgetBySessionId = () => undefined;
        this.lastFocusedWidget = undefined;
        this.revealViewForProvider = unsupported;
        this.getWidgetByInputUri = unsupported;
    }
}, 1 );
registerSingleton(IRemoteExplorerService, class RemoteExplorerService {
    constructor() {
        this.onDidChangeHelpInformation = Event.None;
        this.onDidChangeTargetType = Event.None;
        this.targetType = [];
        this.onDidChangeEditable = Event.None;
        this.setEditable = unsupported;
        this.getEditableData = unsupported;
        this.forward = unsupported;
        this.close = unsupported;
        this.setTunnelInformation = unsupported;
        this.setCandidateFilter = unsupported;
        this.onFoundNewCandidates = unsupported;
        this.restore = unsupported;
        this.enablePortsFeatures = unsupported;
        this.onEnabledPortsFeatures = Event.None;
        this.portsFeaturesEnabled = false;
        this.namedProcesses = new Map();
    }
    get helpInformation() {
        return unsupported();
    }
    get tunnelModel() {
        return unsupported();
    }
}, 1 );
registerSingleton(IAuthenticationService, class AuthenticationService {
    constructor() {
        this.onDidRegisterAuthenticationProvider = Event.None;
        this.onDidUnregisterAuthenticationProvider = Event.None;
        this.onDidChangeSessions = Event.None;
        this.onDidChangeDeclaredProviders = Event.None;
        this.declaredProviders = [];
        this.registerDeclaredAuthenticationProvider = unsupported;
        this.unregisterDeclaredAuthenticationProvider = unsupported;
        this.isAuthenticationProviderRegistered = () => false;
        this.registerAuthenticationProvider = unsupported;
        this.unregisterAuthenticationProvider = unsupported;
        this.getProviderIds = () => [];
        this.getProvider = unsupported;
        this.getSessions = unsupported;
        this.createSession = unsupported;
        this.removeSession = unsupported;
    }
}, 1 );
registerSingleton(IAuthenticationAccessService, class AuthenticationAccessService {
    constructor() {
        this.onDidChangeExtensionSessionAccess = Event.None;
        this.isAccessAllowed = () => false;
        this.readAllowedExtensions = () => [];
        this.updateAllowedExtensions = unsupported;
        this.removeAllowedExtensions = unsupported;
    }
}, 1 );
registerSingleton(IAuthenticationExtensionsService, class AuthenticationExtensionsService {
    constructor() {
        this.updateSessionPreference = unsupported;
        this.getSessionPreference = () => undefined;
        this.removeSessionPreference = unsupported;
        this.selectSession = unsupported;
        this.requestSessionAccess = unsupported;
        this.requestNewSession = unsupported;
    }
}, 1 );
registerSingleton(IAuthenticationUsageService, class AuthenticationUsageService {
    constructor() {
        this.readAccountUsages = unsupported;
        this.removeAccountUsage = unsupported;
        this.addAccountUsage = unsupported;
    }
}, 1 );
registerSingleton(ITimelineService, class TimelineService {
    constructor() {
        this.onDidChangeProviders = Event.None;
        this.onDidChangeTimeline = Event.None;
        this.onDidChangeUri = Event.None;
        this.registerTimelineProvider = unsupported;
        this.unregisterTimelineProvider = unsupported;
        this.getSources = () => [];
        this.getTimeline = unsupported;
        this.setUri = unsupported;
    }
}, 1 );
registerSingleton(ITestService, class TestService {
    constructor() {
        this.onDidCancelTestRun = Event.None;
        this.onWillProcessDiff = Event.None;
        this.onDidProcessDiff = Event.None;
        this.registerTestController = unsupported;
        this.getTestController = () => undefined;
        this.refreshTests = unsupported;
        this.cancelRefreshTests = unsupported;
        this.startContinuousRun = unsupported;
        this.runTests = unsupported;
        this.runResolvedTests = unsupported;
        this.syncTests = unsupported;
        this.cancelTestRun = unsupported;
        this.publishDiff = unsupported;
    }
    get excluded() {
        return unsupported();
    }
    get collection() {
        return unsupported();
    }
    get showInlineOutput() {
        return unsupported();
    }
}, 1 );
registerSingleton(ISecretStorageService, class SecretStorageService {
    constructor() {
        this.onDidChangeSecret = Event.None;
        this.type = 'in-memory';
        this.get = async () => undefined;
        this.set = unsupported;
        this.delete = unsupported;
    }
}, 1 );
registerSingleton(IShareService, class ShareService {
    constructor() {
        this.registerShareProvider = unsupported;
        this.getShareActions = () => [];
        this.provideShare = async () => undefined;
    }
}, 1 );
registerSingleton(IUserDataProfileImportExportService, class UserDataProfileImportExportService {
    constructor() {
        this.createProfile = unsupported;
        this.editProfile = unsupported;
        this.registerProfileContentHandler = unsupported;
        this.unregisterProfileContentHandler = unsupported;
        this.exportProfile = unsupported;
        this.importProfile = unsupported;
        this.showProfileContents = unsupported;
        this.createFromCurrentProfile = unsupported;
        this.createTroubleshootProfile = unsupported;
        this.setProfile = unsupported;
    }
}, 1 );
registerSingleton(IWorkbenchIssueService, class WorkbenchIssueService {
    constructor() {
        this.registerIssueDataProvider = unsupported;
        this.openReporter = unsupported;
        this.openProcessExplorer = unsupported;
        this.registerIssueUriRequestHandler = unsupported;
    }
}, 1 );
registerSingleton(ISCMViewService, class SCMViewService {
    constructor() {
        this.repositories = [];
        this.onDidChangeRepositories = Event.None;
        this.visibleRepositories = [];
        this.onDidChangeVisibleRepositories = Event.None;
        this.isVisible = () => false;
        this.toggleVisibility = unsupported;
        this.toggleSortKey = unsupported;
        this.focusedRepository = undefined;
        this.onDidFocusRepository = Event.None;
        this.focus = unsupported;
    }
    get menus() {
        return unsupported();
    }
}, 1 );
registerSingleton(INotebookExecutionStateService, class NotebookExecutionStateService {
    constructor() {
        this.onDidChangeExecution = Event.None;
        this.onDidChangeLastRunFailState = Event.None;
        this.forceCancelNotebookExecutions = unsupported;
        this.getCellExecutionsForNotebook = unsupported;
        this.getCellExecutionsByHandleForNotebook = unsupported;
        this.getCellExecution = unsupported;
        this.createCellExecution = unsupported;
        this.getExecution = unsupported;
        this.createExecution = unsupported;
        this.getLastFailedCellForNotebook = unsupported;
    }
}, 1 );
registerSingleton(ITestProfileService, class TestProfileService {
    constructor() {
        this.onDidChange = Event.None;
        this.addProfile = unsupported;
        this.updateProfile = unsupported;
        this.removeProfile = unsupported;
        this.capabilitiesForTest = unsupported;
        this.configure = unsupported;
        this.all = () => [];
        this.getGroupDefaultProfiles = () => [];
        this.setGroupDefaultProfiles = unsupported;
        this.getControllerProfiles = () => [];
    }
}, 1 );
registerSingleton(IEncryptionService, class EncryptionService {
    constructor() {
        this.setUsePlainTextEncryption = unsupported;
        this.getKeyStorageProvider = unsupported;
        this.encrypt = unsupported;
        this.decrypt = unsupported;
        this.isEncryptionAvailable = unsupported;
    }
}, 1 );
registerSingleton(ITestResultService, class TestResultService {
    constructor() {
        this.onResultsChanged = Event.None;
        this.onTestChanged = Event.None;
        this.results = [];
        this.clear = unsupported;
        this.createLiveResult = unsupported;
        this.push = unsupported;
        this.getResult = () => undefined;
        this.getStateById = () => undefined;
    }
}, 1 );
registerSingleton(ITestResultStorage, class TestResultStorage {
    constructor() {
        this.read = unsupported;
        this.persist = unsupported;
    }
}, 1 );
registerSingleton(ITestingDecorationsService, class TestingDecorationsService {
    constructor() {
        this.onDidChange = Event.None;
        this.invalidateResultMessage = unsupported;
        this.syncDecorations = unsupported;
        this.getDecoratedTestPosition = unsupported;
    }
}, 1 );
registerSingleton(IUserDataInitializationService, class UserDataInitializationService {
    constructor() {
        this.requiresInitialization = async () => false;
        this.whenInitializationFinished = async () => { };
        this.initializeRequiredResources = async () => { };
        this.initializeInstalledExtensions = async () => { };
        this.initializeOtherResources = async () => { };
    }
}, 1 );
registerSingleton(IDiagnosticsService, NullDiagnosticsService, 1 );
registerSingleton(INotebookSearchService, class NotebookSearchService {
    notebookSearch() {
        return {
            openFilesToScan: new ResourceSet(),
            completeData: Promise.resolve({
                results: [],
                messages: []
            }),
            allScannedFiles: Promise.resolve(new ResourceSet())
        };
    }
}, 1 );
registerSingleton(ILanguageModelsService, class LanguageModelsService {
    constructor() {
        this.computeTokenLength = unsupported;
        this.onDidChangeLanguageModels = Event.None;
        this.getLanguageModelIds = () => [];
        this.lookupLanguageModel = () => undefined;
        this.registerLanguageModelChat = unsupported;
        this.makeLanguageModelChatRequest = unsupported;
    }
}, 1 );
registerSingleton(IChatSlashCommandService, class ChatSlashCommandService {
    constructor() {
        this.onDidChangeCommands = unsupported;
        this.registerSlashData = unsupported;
        this.registerSlashCallback = unsupported;
        this.registerSlashCommand = unsupported;
        this.executeCommand = unsupported;
        this.getCommands = unsupported;
        this.hasCommand = unsupported;
    }
}, 1 );
registerSingleton(IChatVariablesService, class ChatVariablesService {
    constructor() {
        this.getVariable = () => undefined;
        this.resolveVariable = async () => [];
        this.getDynamicVariables = unsupported;
        this.getDynamicReferences = unsupported;
        this.registerVariable = unsupported;
        this.getVariables = unsupported;
        this.resolveVariables = unsupported;
        this.hasVariable = unsupported;
    }
}, 1 );
registerSingleton(IAiRelatedInformationService, class AiRelatedInformationService {
    constructor() {
        this.isEnabled = () => false;
        this.getRelatedInformation = unsupported;
        this.registerAiRelatedInformationProvider = unsupported;
    }
}, 1 );
registerSingleton(IAiEmbeddingVectorService, class AiEmbeddingVectorService {
    constructor() {
        this.isEnabled = () => false;
        this.getEmbeddingVector = unsupported;
        this.registerAiEmbeddingVectorProvider = unsupported;
    }
}, 1 );
registerSingleton(ISignService, (_a = class SignService {
        async createNewMessage(value) {
            const id = String(_a._nextId++);
            return {
                id,
                data: value
            };
        }
        async validate() {
            return true;
        }
        async sign(value) {
            return value;
        }
    },
    _a._nextId = 1,
    _a), 1 );
registerSingleton(ITestingContinuousRunService, class TestingContinuousRunService {
    constructor() {
        this.lastRunProfileIds = new Set();
        this.onDidChange = Event.None;
        this.isSpecificallyEnabledFor = () => false;
        this.isEnabledForAParentOf = () => false;
        this.isEnabledForAChildOf = () => false;
        this.isEnabled = () => false;
        this.start = unsupported;
        this.stop = unsupported;
    }
}, 1 );
registerSingleton(ITestExplorerFilterState, class TestExplorerFilterState {
    constructor() {
        this.onDidRequestInputFocus = Event.None;
        this.focusInput = unsupported;
        this.setText = unsupported;
        this.isFilteringFor = () => false;
        this.toggleFilteringFor = unsupported;
    }
    get text() { return unsupported(); }
    get reveal() { return unsupported(); }
    get globList() { return unsupported(); }
    get includeTags() { return unsupported(); }
    get excludeTags() { return unsupported(); }
    get fuzzy() { return unsupported(); }
}, 1 );
registerSingleton(ITestingPeekOpener, class TestingPeekOpener {
    constructor() {
        this.tryPeekFirstError = unsupported;
        this.peekUri = unsupported;
        this.openCurrentInEditor = unsupported;
        this.open = unsupported;
        this.closeAllPeeks = unsupported;
    }
    get historyVisible() { return unsupported(); }
}, 1 );
registerSingleton(IAuxiliaryWindowService, class AuxiliaryWindowService {
    constructor() {
        this.getWindow = () => undefined;
        this.onDidOpenAuxiliaryWindow = Event.None;
        this.hasWindow = () => false;
        this.open = unsupported;
    }
}, 1 );
registerSingleton(ISpeechService, class SpeechService {
    constructor() {
        this.onDidChangeHasSpeechProvider = Event.None;
        this.onDidStartSpeechToTextSession = Event.None;
        this.onDidEndSpeechToTextSession = Event.None;
        this.hasActiveSpeechToTextSession = false;
        this.onDidStartKeywordRecognition = Event.None;
        this.onDidEndKeywordRecognition = Event.None;
        this.hasActiveKeywordRecognition = false;
        this.recognizeKeyword = unsupported;
        this.onDidRegisterSpeechProvider = Event.None;
        this.onDidUnregisterSpeechProvider = Event.None;
        this.hasSpeechProvider = false;
        this.registerSpeechProvider = unsupported;
        this.createSpeechToTextSession = unsupported;
    }
}, 1 );
registerSingleton(ITestCoverageService, class TestCoverageService {
    constructor() {
        this.openCoverage = unsupported;
        this.closeCoverage = unsupported;
    }
    get selected() {
        return unsupported();
    }
}, 1 );
registerSingleton(IChatAccessibilityService, class ChatAccessibilityService {
    constructor() {
        this.acceptRequest = unsupported;
        this.acceptResponse = unsupported;
    }
}, 1 );
registerSingleton(IChatWidgetHistoryService, class ChatWidgetHistoryService {
    constructor() {
        this.onDidClearHistory = Event.None;
        this.clearHistory = unsupported;
        this.getHistory = () => [];
        this.saveHistory = unsupported;
    }
}, 1 );
registerSingleton(IChatCodeBlockContextProviderService, class ChatCodeBlockContextProviderService {
    constructor() {
        this.providers = [];
        this.registerProvider = unsupported;
    }
}, 1 );
registerSingleton(IInlineChatSessionService, class InlineChatSessionService {
    constructor() {
        this.onDidMoveSession = Event.None;
        this.onDidMoveSessio = Event.None;
        this.onDidStashSession = Event.None;
        this.moveSession = unsupported;
        this.getCodeEditor = unsupported;
        this.stashSession = unsupported;
        this.onWillStartSession = Event.None;
        this.onDidEndSession = Event.None;
        this.createSession = unsupported;
        this.getSession = () => undefined;
        this.releaseSession = unsupported;
        this.registerSessionKeyComputer = unsupported;
        this.recordings = unsupported;
        this.dispose = unsupported;
    }
}, 1 );
registerSingleton(INotebookEditorWorkerService, class NotebookEditorWorkerService {
    constructor() {
        this.canComputeDiff = () => false;
        this.computeDiff = unsupported;
        this.canPromptRecommendation = async () => false;
    }
}, 1 );
registerSingleton(INotebookKernelHistoryService, class NotebookKernelHistoryService {
    constructor() {
        this.getKernels = unsupported;
        this.addMostRecentKernel = unsupported;
    }
}, 1 );
registerSingleton(INotebookExecutionService, class NotebookExecutionService {
    constructor() {
        this.executeNotebookCells = unsupported;
        this.cancelNotebookCells = unsupported;
        this.cancelNotebookCellHandles = unsupported;
        this.registerExecutionParticipant = unsupported;
    }
}, 1 );
registerSingleton(INotebookKeymapService, class NotebookKeymapService {
}, 1 );
registerSingleton(INotebookLoggingService, class NotebookLoggingService {
    constructor() {
        this.info = unsupported;
        this.debug = unsupported;
    }
}, 1 );
registerSingleton(IWalkthroughsService, class WalkthroughsService {
    constructor() {
        this.onDidAddWalkthrough = Event.None;
        this.onDidRemoveWalkthrough = Event.None;
        this.onDidChangeWalkthrough = Event.None;
        this.onDidProgressStep = Event.None;
        this.getWalkthroughs = unsupported;
        this.getWalkthrough = unsupported;
        this.registerWalkthrough = unsupported;
        this.progressByEvent = unsupported;
        this.progressStep = unsupported;
        this.deprogressStep = unsupported;
        this.markWalkthroughOpened = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncStoreManagementService, class UserDataSyncStoreManagementService {
    constructor() {
        this.onDidChangeUserDataSyncStore = Event.None;
        this.userDataSyncStore = undefined;
        this.switch = unsupported;
        this.getPreviousUserDataSyncStore = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncStoreService, class UserDataSyncStoreService {
    constructor() {
        this.onDidChangeDonotMakeRequestsUntil = Event.None;
        this.donotMakeRequestsUntil = undefined;
        this.onTokenFailed = Event.None;
        this.onTokenSucceed = Event.None;
        this.setAuthToken = unsupported;
        this.manifest = unsupported;
        this.readResource = unsupported;
        this.writeResource = unsupported;
        this.deleteResource = unsupported;
        this.getAllResourceRefs = unsupported;
        this.resolveResourceContent = unsupported;
        this.getAllCollections = unsupported;
        this.createCollection = unsupported;
        this.deleteCollection = unsupported;
        this.getActivityData = unsupported;
        this.clear = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncLogService, class UserDataSyncLogService {
    constructor() {
        this.onDidChangeLogLevel = Event.None;
        this.getLevel = unsupported;
        this.setLevel = unsupported;
        this.trace = unsupported;
        this.debug = unsupported;
        this.info = unsupported;
        this.warn = unsupported;
        this.error = unsupported;
        this.flush = unsupported;
        this.dispose = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncService, class UserDataSyncService {
    constructor() {
        this.status = "uninitialized" ;
        this.onDidChangeStatus = Event.None;
        this.conflicts = [];
        this.onDidChangeConflicts = Event.None;
        this.onDidChangeLocal = Event.None;
        this.onSyncErrors = Event.None;
        this.onDidChangeLastSyncTime = Event.None;
        this.onDidResetRemote = Event.None;
        this.onDidResetLocal = Event.None;
        this.createSyncTask = unsupported;
        this.createManualSyncTask = unsupported;
        this.resolveContent = unsupported;
        this.accept = unsupported;
        this.reset = unsupported;
        this.resetRemote = unsupported;
        this.cleanUpRemoteData = unsupported;
        this.resetLocal = unsupported;
        this.hasLocalData = unsupported;
        this.hasPreviouslySynced = unsupported;
        this.replace = unsupported;
        this.saveRemoteActivityData = unsupported;
        this.extractActivityData = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncMachinesService, class UserDataSyncMachinesService {
    constructor() {
        this.onDidChange = Event.None;
        this.getMachines = unsupported;
        this.addCurrentMachine = unsupported;
        this.removeCurrentMachine = unsupported;
        this.renameMachine = unsupported;
        this.setEnablements = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncResourceProviderService, class UserDataSyncResourceProviderService {
    constructor() {
        this.getRemoteSyncedProfiles = unsupported;
        this.getLocalSyncedProfiles = unsupported;
        this.getRemoteSyncResourceHandles = unsupported;
        this.getLocalSyncResourceHandles = unsupported;
        this.getAssociatedResources = unsupported;
        this.getMachineId = unsupported;
        this.getLocalSyncedMachines = unsupported;
        this.resolveContent = unsupported;
        this.resolveUserDataSyncResource = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncLocalStoreService, class UserDataSyncLocalStoreService {
    constructor() {
        this.writeResource = unsupported;
        this.getAllResourceRefs = unsupported;
        this.resolveResourceContent = unsupported;
    }
}, 1 );
registerSingleton(IUserDataSyncUtilService, class UserDataSyncUtilService {
    constructor() {
        this.resolveUserBindings = unsupported;
        this.resolveFormattingOptions = unsupported;
        this.resolveDefaultIgnoredSettings = unsupported;
    }
}, 1 );
registerSingleton(IUserDataProfileManagementService, class UserDataProfileManagementService {
    constructor() {
        this.createAndEnterProfile = unsupported;
        this.createAndEnterTransientProfile = unsupported;
        this.removeProfile = unsupported;
        this.updateProfile = unsupported;
        this.switchProfile = unsupported;
        this.getBuiltinProfileTemplates = unsupported;
    }
}, 1 );
registerSingleton(IWorkingCopyHistoryService, class WorkingCopyHistoryService {
    constructor() {
        this.onDidAddEntry = Event.None;
        this.onDidChangeEntry = Event.None;
        this.onDidReplaceEntry = Event.None;
        this.onDidRemoveEntry = Event.None;
        this.onDidMoveEntries = Event.None;
        this.onDidRemoveEntries = Event.None;
        this.addEntry = unsupported;
        this.updateEntry = unsupported;
        this.removeEntry = unsupported;
        this.moveEntries = unsupported;
        this.getEntries = unsupported;
        this.getAll = unsupported;
        this.removeAll = unsupported;
    }
}, 1 );
registerSingleton(IInlineChatSavingService, class InlineChatSavingService {
    constructor() {
        this.markChanged = unsupported;
    }
}, 1 );
registerSingleton(INotebookDocumentService, class NotebookDocumentService {
    constructor() {
        this.getNotebook = () => undefined;
        this.addNotebookDocument = unsupported;
        this.removeNotebookDocument = unsupported;
    }
}, 1 );
registerSingleton(IDebugVisualizerService, class DebugVisualizerService {
    constructor() {
        this.registerTree = unsupported;
        this.getVisualizedNodeFor = unsupported;
        this.getVisualizedChildren = unsupported;
        this.editTreeItem = unsupported;
        this.getApplicableFor = unsupported;
        this.register = unsupported;
    }
}, 1 );
registerSingleton(IEditSessionsLogService, class EditSessionsLogService {
    constructor() {
        this.onDidChangeLogLevel = Event.None;
        this.getLevel = unsupported;
        this.setLevel = unsupported;
        this.trace = unsupported;
        this.debug = unsupported;
        this.info = unsupported;
        this.warn = unsupported;
        this.error = unsupported;
        this.flush = unsupported;
        this.dispose = unsupported;
    }
}, 1 );
registerSingleton(IEditSessionsStorageService, class EditSessionsWorkbenchService {
    constructor() {
        this.SIZE_LIMIT = 0;
        this.isSignedIn = false;
        this.onDidSignIn = Event.None;
        this.onDidSignOut = Event.None;
        this.storeClient = undefined;
        this.lastReadResources = new Map();
        this.lastWrittenResources = new Map();
        this.initialize = unsupported;
        this.read = unsupported;
        this.write = unsupported;
        this.delete = unsupported;
        this.list = unsupported;
        this.getMachineById = unsupported;
    }
}, 1 );
registerSingleton(IMultiDiffSourceResolverService, class MultiDiffSourceResolverService {
    constructor() {
        this.registerResolver = () => Disposable.None;
        this.resolve = async () => undefined;
    }
}, 1 );
registerSingleton(IWorkspaceTagsService, NoOpWorkspaceTagsService, 1 );
registerSingleton(IExtensionFeaturesManagementService, class ExtensionFeaturesManagementService {
    constructor() {
        this.onDidChangeEnablement = Event.None;
        this.isEnabled = () => true;
        this.setEnablement = unsupported;
        this.getEnablementData = unsupported;
        this.getAccess = unsupported;
        this.onDidChangeAccessData = Event.None;
        this.getAccessData = () => undefined;
        this.setStatus = unsupported;
    }
}, 1 );
registerSingleton(IEditorPaneService, class EditorPaneService {
    constructor() {
        this.onWillInstantiateEditorPane = Event.None;
        this.didInstantiateEditorPane = () => false;
    }
}, 1 );
registerSingleton(IWorkspaceIdentityService, class WorkspaceIdentityService {
    constructor() {
        this.matches = unsupported;
        this.getWorkspaceStateFolders = unsupported;
    }
}, 1 );
registerSingleton(IDefaultLogLevelsService, class DefaultLogLevelsService {
    constructor() {
        this.onDidChangeDefaultLogLevels = Event.None;
        this.getDefaultLogLevel = async () => LogLevel.Off;
        this.getDefaultLogLevels = unsupported;
        this.setDefaultLogLevel = unsupported;
        this.migrateLogLevels = unsupported;
    }
}, 1 );
registerSingleton(ICustomEditorLabelService, class CustomEditorLabelService {
    constructor() {
        this.onDidChange = Event.None;
        this.getName = () => undefined;
    }
}, 1 );
registerSingleton(ITroubleshootIssueService, class TroubleshootIssueService {
    constructor() {
        this.isActive = () => false;
        this.start = unsupported;
        this.resume = unsupported;
        this.stop = unsupported;
    }
}, 1 );
